import React from "react";
import "../styles/shared-ui.css";
import "../styles/form.css";
import "../styles/modal.css";
import "../styles/process-builder.css";
import { useAppContext } from "../context/AppContext";
import { getCompanyNames } from "../services/companyService";
import {
  getTasksByCompanyId,
  getTaskWithRelations,
} from "../services/taskService";
import {
  createProcess,
  getProcessWithRelations,
  updateProcess,
  getProcessesByCompanyId,
} from "../services/processService";
import { getProcessCategories } from "../services/processCategoryService";
import { getProcessStatuses } from "../services/processStatusService";
import { mapJobTasksToWorkflowJobs } from "../components/ui/WorkflowJobChips";
import ConfirmModal from "../components/ui/ConfirmModal";
import TaskDetailModal from "../components/ui/TaskDetailModal";
import TaskCreateModal from "../components/ui/TaskCreateModal";
import { deleteGateway, clearStoredBpmnXml } from "../services/bpmnService";
import { toast } from "../utils/toast";
import {
  exclusiveSkipsConvergence,
  gatewayRequiresConvergence,
} from "../utils/gatewayConvergence";

// Process Builder: company -> process -> drag tasks and gateways from the side
// panel onto the process's path. Everything (placement, branch targets,
// convergence) is set by dropping onto a slot rather than picking from a
// dropdown. Reads/writes the same workflow[]/gateways[] contract as
// ProcessEditPage so both stay in sync on the same process.

const START_EVENT_GATEWAY_ID = "__START_EVENT__";
const normalizeGatewayAfterTaskId = (value) =>
  String(value) === START_EVENT_GATEWAY_ID
    ? null
    : value
      ? Number(value)
      : null;

const normalizeGatewayType = (type) =>
  String(type || "")
    .trim()
    .toUpperCase();

const needsConvergence = (type) => {
  const t = normalizeGatewayType(type);
  return (
    t === "PARALLEL" || t === "INCLUSIVE" || t === "EXCLUSIVE" || t === "COMPLEX"
  );
};

const requiresConvergence = (gatewayOrType) => {
  if (gatewayOrType && typeof gatewayOrType === "object") {
    return gatewayRequiresConvergence(gatewayOrType, needsConvergence);
  }
  return needsConvergence(gatewayOrType);
};

const getConvergeAtTaskId = (gateway) => {
  const raw = gateway?.convergeAtTaskId ?? gateway?.converge_at_task_id ?? "";
  if (raw === "" || raw == null || Number(raw) === 0) return "";
  return String(raw);
};
const getConvergeGatewayName = (gateway) =>
  gateway?.convergeGatewayName ?? gateway?.converge_gateway_name ?? "";
const getConvergeToEnd = (gateway) =>
  !!(gateway?.convergeToEnd ?? gateway?.converge_to_end);
const hasConvergeTarget = (gateway) =>
  !!(
    getConvergeAtTaskId(gateway) ||
    getConvergeToEnd(gateway) ||
    gateway?.convergeAtGatewayId
  );

// Mirrors GatewaySection & GatewayConfigModal's gateway type metadata (also
// used by the read-only branching diagram in ProcessViewPage), so gateway
// colors/symbols stay consistent across the app's BPMN screens.
const GATEWAY_TYPE_INFO = {
  EXCLUSIVE: { symbol: "✕", color: "#dc2626", label: "Exclusive" },
  PARALLEL: { symbol: "+", color: "#16a34a", label: "Parallel" },
  INCLUSIVE: { symbol: "○", color: "#d97706", label: "Inclusive" },
  EVENT_BASED: { symbol: "⬡", color: "#8b5cf6", label: "Event-Based" },
  COMPLEX: { symbol: "✱", color: "#9b59b6", label: "Complex" },
};
const getGatewayTypeInfo = (type) =>
  GATEWAY_TYPE_INFO[normalizeGatewayType(type)] || GATEWAY_TYPE_INFO.EXCLUSIVE;

// COMPLEX stays in GATEWAY_TYPE_INFO so processes that already carry one keep
// rendering correctly, but it is no longer offered when picking a gateway.
const GATEWAY_TYPE_ORDER = ["EXCLUSIVE", "PARALLEL", "INCLUSIVE", "EVENT_BASED"];

// Types shown in the gateway's own type picker: the selectable set, plus the
// gateway's current type when that is a legacy one, so it still reads correctly
// and can be switched away from.
const gatewayTypeChoices = (currentType) => {
  const t = normalizeGatewayType(currentType);
  return GATEWAY_TYPE_ORDER.includes(t) || !GATEWAY_TYPE_INFO[t]
    ? GATEWAY_TYPE_ORDER
    : [...GATEWAY_TYPE_ORDER, t];
};

const needsConditions = (type) => {
  const t = normalizeGatewayType(type);
  return t === "EXCLUSIVE" || t === "INCLUSIVE" || t === "EVENT_BASED";
};
const stepIdOfRow = (row) =>
  row?.isChildProcess ? row.child_process_id : row?.task_id;

const createGatewayId = () =>
  `gw-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
const createBranchId = () =>
  `br-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;

const extractCreatedProcessId = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  const candidates = [
    payload.process_id,
    payload.id,
    payload.data?.process_id,
    payload.data?.id,
    payload.process?.process_id,
    payload.process?.id,
    payload.data?.process?.process_id,
    payload.data?.process?.id,
  ];
  const found = candidates.find((value) => value != null && value !== "");
  return found != null ? String(found) : null;
};

// ---- Branching canvas: layout + connector geometry -------------------------

const CANVAS_COL_GAP = 250;
const CANVAS_LANE_GAP = 150;
const CANVAS_PAD_X = 90;
const CANVAS_PAD_Y = 90;

const halfWidthOf = (kind) => {
  if (kind === "start" || kind === "end") return 28;
  if (kind === "gateway") return 24;
  return 95;
};
// Mirrors the rendered box sizes in process-builder.css so a drop can be
// hit-tested against the node it landed on.
const halfHeightOf = (kind) => {
  if (kind === "start" || kind === "end") return 28;
  if (kind === "gateway") return 24;
  return 39;
};
const START_KEY = "__start__";
const END_KEY = "__end__";

/**
 * Lays the process out as a spine with vertical fan-out at each gateway —
 * the shape a BPMN reader expects: Start → steps → ◇ splits into stacked
 * branch lanes → they rejoin → continue.
 *
 * Safety net that the earlier attempt lacked: every node claims a unique
 * (column, lane) cell via `occupied`. If any structure — loops, repeated
 * targets, malformed branches — would land two nodes in one cell, the later
 * one is pushed to the next free lane instead of drawing on top. So the
 * canvas can degrade into an odd-looking layout, but never an overlapping,
 * unreadable one.
 */
const buildCanvasModel = (workflow, gateways, labelOf) => {
  const nodes = new Map();
  const edges = [];
  const occupied = new Set();

  const rowKey = (row) =>
    row.isChildProcess ? `sub-${row.child_process_id}` : `task-${row.task_id}`;
  const rowAfterId = (row) =>
    row.isChildProcess ? row.child_process_id : row.task_id;

  const idxByKey = new Map();
  workflow.forEach((r, i) => idxByKey.set(rowKey(r), i));
  const keyAt = (i) =>
    i < 0 ? START_KEY : i >= workflow.length ? END_KEY : rowKey(workflow[i]);

  const gwKey = (g) => `gw-${g.id}`;
  const gatewayAfter = (afterId) =>
    afterId == null
      ? null
      : gateways.find((g) => String(g.afterTaskId) === String(afterId));
  const anchorKeyOfGateway = (g) => {
    if (!g) return null;
    if (String(g.afterTaskId) === START_EVENT_GATEWAY_ID) return START_KEY;
    return resolveTrunkKey(g.afterTaskId);
  };

  const place = (key, node, col, preferredLane) => {
    if (nodes.has(key)) return nodes.get(key);
    let lane = preferredLane;
    let step = 0;
    while (occupied.has(`${col}:${lane}`)) {
      step += 1;
      lane = preferredLane + step;
    }
    occupied.add(`${col}:${lane}`);
    const placed = { ...node, key, col, lane };
    nodes.set(key, placed);
    return placed;
  };

  const addEdge = (from, to, label = "", extra = {}) => {
    if (!from || !to || from === to) return;
    edges.push({ from, to, label, ...extra });
  };

  const markChainEnd = (keys, endIdx) => {
    keys.forEach((k) => {
      const n = nodes.get(k);
      if (n && n.chainEndIdx == null) n.chainEndIdx = endIdx;
    });
  };

  const placeTrunkNode = (i, col, lane) => {
    const row = workflow[i];
    return place(
      rowKey(row),
      {
        kind: row.isChildProcess ? "subprocess" : "task",
        name: labelOf(row),
        trunkIndex: i,
      },
      col,
      lane,
    );
  };

  const resolveTrunkKey = (raw) => {
    if (raw == null || raw === "") return null;
    if (idxByKey.has(`task-${raw}`)) return `task-${raw}`;
    if (idxByKey.has(`sub-${raw}`)) return `sub-${raw}`;
    return null;
  };

  // A branch/endpoint with no target task ends the process. Named end events
  // (e.g. "Rejected", "Cancelled") get their own terminal node so each path
  // reads as its own outcome instead of every branch funneling into one
  // generic "End" box — same name across gateways still shares one node,
  // since that's the same conceptual end event.
  const endKeyFor = (endEventName) => {
    const trimmed = (endEventName || "").trim();
    if (!trimmed) return END_KEY;
    const slug = trimmed.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    return `end-${slug}`;
  };

  const usedGateways = new Set();
  let budget = (workflow.length + gateways.length) * 4 + 40;

  /**
   * Lays out steps [fromIdx, untilIdx) left-to-right along `lane`, recursing
   * whenever a gateway is attached to one of those steps.
   * Returns the chain's tail keys, the next free column and the next index.
   *
   * `incomingMeta` tags the very first edge of the chain. Branch chains pass
   * the gateway/branch they hang off so the UI can turn that edge into an
   * insertion point that re-points the branch (a plain array splice would
   * orphan the new step, since branches address steps by id, not position).
   */
  const layoutChain = (
    fromIdx,
    untilIdx,
    col,
    lane,
    incoming,
    incomingLabel,
    incomingMeta,
  ) => {
    let curCol = col;
    let prevKeys = incoming;
    let label = incomingLabel || "";
    let meta = incomingMeta || null;
    let j = fromIdx;

    while (j < untilIdx && budget-- > 0) {
      const key = keyAt(j);
      const stepIdx = j;
      if (!nodes.has(key)) placeTrunkNode(stepIdx, curCol, lane);
      const edgeLabel = label;
      const edgeMeta = meta
        ? { ...meta }
        : { spine: true, insertIndex: stepIdx };
      prevKeys.forEach((p) => addEdge(p, key, edgeLabel, edgeMeta));
      label = "";
      meta = null;
      prevKeys = [key];
      curCol += 1;
      j += 1;

      const gw = gatewayAfter(rowAfterId(workflow[stepIdx]));
      if (gw && !usedGateways.has(gw.id)) {
        const res = layoutGateway(gw, j, untilIdx, curCol, lane, prevKeys);
        prevKeys = res.tails;
        curCol = Math.max(curCol, res.col);
        j = Math.max(j, res.idx);
      }
    }
    // Where this chain's ownership of the array stops. Appending to a branch
    // means splicing at *its* untilIdx, not at the end of the workflow —
    // otherwise the step lands inside whichever branch is laid out last.
    // Inner (branch) chains claim their tails before the outer chain sees
    // them, so the most specific value wins.
    if (j > fromIdx) markChainEnd(prevKeys, untilIdx);
    return { tails: prevKeys, col: curCol, idx: j };
  };

  /**
   * Places a gateway and fans its branches into separate lanes. Each branch
   * owns a *chain* of consecutive steps — the same shape the paths API
   * returns as `branch.nodes[]` — running until the next branch starts or
   * the declared join point.
   */
  function layoutGateway(gw, fromIdx, untilIdx, col, lane, incoming) {
    usedGateways.add(gw.id);
    const gk = gwKey(gw);
    const info = getGatewayTypeInfo(gw.type);
    place(
      gk,
      {
        kind: "gateway",
        name: gw.name || info.label,
        gatewayId: gw.id,
        gwType: normalizeGatewayType(gw.type),
      },
      col,
      lane,
    );
    // Tagged so the UI can offer an insertion point immediately *before* the
    // diamond — dropping there also re-anchors the gateway onto the new step.
    incoming.forEach((p) =>
      addEdge(p, gk, "", { preGateway: true, gatewayId: gw.id }),
    );
    const afterCol = col + 1;

    const branchList = [
      ...(gw.branches || []).map((b) => ({
        target: b.targetTaskId,
        label: b.condition || "",
        endEventName: b.endEventName || "",
        branchId: b.id,
        branchKind: "branch",
      })),
      ...(gw.endpoints || []).map((e) => ({
        target: e.endTaskId,
        label: e.condition || "",
        endEventName: e.endEventName || "",
        branchId: e.id,
        branchKind: "endpoint",
      })),
    ];

    const forward = [];
    const offSpine = [];
    branchList.forEach((b) => {
      const raw = b.target;
      if (!raw) {
        offSpine.push({ ...b, toKey: endKeyFor(b.endEventName) });
        return;
      }
      if (typeof raw === "string" && raw.startsWith("__GW__")) {
        const tg = gateways.find((x) => String(x.id) === raw.slice(6));
        offSpine.push({ ...b, toKey: tg ? gwKey(tg) : END_KEY });
        return;
      }
      const k = resolveTrunkKey(raw);
      if (!k) {
        offSpine.push({ ...b, toKey: END_KEY });
        return;
      }
      const jj = idxByKey.get(k);
      if (jj >= fromIdx && jj < untilIdx) forward.push({ ...b, j: jj, toKey: k });
      else offSpine.push({ ...b, toKey: k, backward: jj < fromIdx });
    });

    offSpine.forEach((b) => {
      if (b.toKey !== END_KEY && b.toKey.startsWith("end-")) {
        place(b.toKey, { kind: "end", name: b.endEventName }, afterCol, lane);
      }
      addEdge(gk, b.toKey, b.label, {
        branch: true,
        offSpine: true,
        backward: !!b.backward,
        fromGatewayId: gw.id,
        branchId: b.branchId,
        branchKind: b.branchKind,
      });
    });

    if (forward.length === 0) {
      return { tails: [gk], col: afterCol, idx: fromIdx };
    }

    forward.sort((a, b) => a.j - b.j);
    const n = forward.length;

    let convergeIdx = null;
    const convergeKey = gw.convergeAtTaskId
      ? resolveTrunkKey(gw.convergeAtTaskId)
      : null;
    if (convergeKey) {
      const c = idxByKey.get(convergeKey);
      if (c > forward[n - 1].j && c <= untilIdx) convergeIdx = c;
    }
    const chainEnd = convergeIdx != null ? convergeIdx : untilIdx;

    const tails = [];
    let maxCol = afterCol;
    let lastIdx = fromIdx;

    forward.forEach((f, k) => {
      const branchLane = lane + (k - (n - 1) / 2);
      const stop = k + 1 < n ? forward[k + 1].j : chainEnd;
      const res = layoutChain(
        f.j,
        Math.max(f.j + 1, Math.min(stop, untilIdx)),
        afterCol,
        branchLane,
        [gk],
        f.label,
        {
          branch: true,
          fromGatewayId: gw.id,
          branchId: f.branchId,
          branchKind: f.branchKind,
        },
      );
      tails.push(...res.tails);
      maxCol = Math.max(maxCol, res.col);
      lastIdx = Math.max(lastIdx, res.idx);
    });

    return {
      tails: tails.length ? tails : [gk],
      col: maxCol,
      idx: Math.max(lastIdx, fromIdx),
    };
  }

  place(START_KEY, { kind: "start", name: "Start" }, 0, 0);

  let tails = [START_KEY];
  let col = 1;
  let idx = 0;

  const startGateway = gatewayAfter(START_EVENT_GATEWAY_ID);
  if (startGateway) {
    const res = layoutGateway(
      startGateway,
      0,
      workflow.length,
      col,
      0,
      [START_KEY],
    );
    tails = res.tails;
    col = res.col;
    idx = res.idx;
  }

  const main = layoutChain(idx, workflow.length, col, 0, tails, "");

  place(END_KEY, { kind: "end", name: "End" }, main.col, 0);
  main.tails.forEach((p) =>
    addEdge(p, END_KEY, "", { spine: true, insertIndex: workflow.length }),
  );

  // Defensive: anything the walk never reached still gets a cell so no step
  // silently disappears from the canvas. Flagged as `orphan` so the UI can say
  // "not connected" rather than leaving a box floating with no explanation.
  workflow.forEach((row, j) => {
    if (nodes.has(rowKey(row))) return;
    placeTrunkNode(j, main.col, 1).orphan = true;
  });
  gateways.forEach((g) => {
    if (nodes.has(gwKey(g))) return;
    const info = getGatewayTypeInfo(g.type);
    const anchorKey = anchorKeyOfGateway(g);
    const anchorNode = anchorKey ? nodes.get(anchorKey) : null;
    const col = anchorNode ? anchorNode.col + 1 : main.col;
    const lane = anchorNode ? anchorNode.lane : 2;
    place(
      gwKey(g),
      {
        kind: "gateway",
        name: g.name || info.label,
        gatewayId: g.id,
        gwType: normalizeGatewayType(g.type),
        orphan: !anchorNode,
      },
      col,
      lane,
    );
    if (anchorKey && anchorNode) {
      addEdge(anchorKey, gwKey(g), "", {
        preGateway: true,
        gatewayId: g.id,
      });
      (g.branches || []).forEach((b) => {
        const raw = b.targetTaskId;
        const label = b.condition || "";
        let toKey = END_KEY;
        let backward = false;
        if (raw) {
          if (typeof raw === "string" && raw.startsWith("__GW__")) {
            const target = gateways.find((x) => String(x.id) === raw.slice(6));
            toKey = target ? gwKey(target) : END_KEY;
          } else {
            const trunkKey = resolveTrunkKey(raw);
            if (trunkKey) {
              toKey = trunkKey;
              const anchorIdx = idxByKey.get(anchorKey);
              const targetIdx = idxByKey.get(trunkKey);
              backward =
                anchorIdx != null && targetIdx != null && targetIdx <= anchorIdx;
            }
          }
        }
        addEdge(gwKey(g), toKey, label, {
          branch: true,
          backward,
          fromGatewayId: g.id,
          branchId: b.id,
          branchKind: "branch",
        });
      });
      (g.endpoints || []).forEach((e) => {
        const toKey = e.endTaskId ? resolveTrunkKey(e.endTaskId) || END_KEY : END_KEY;
        addEdge(gwKey(g), toKey, e.condition || e.endEventName || "", {
          branch: true,
          fromGatewayId: g.id,
          branchId: e.id,
          branchKind: "endpoint",
        });
      });
    }
  });

  // Safety net: a gateway whose anchor task resolves to a real, placed node
  // should always show a connector back to it. The walk above computes this
  // as a byproduct of tracing chains/branches, and can miss it in edge cases
  // (e.g. an anchor that sits right on a convergence boundary) — silently
  // leaving a configured gateway floating on the canvas (BUG-03). Rather than
  // chase every such case in the walk, backfill any anchored gateway that
  // still has no incoming edge once layout is done.
  gateways.forEach((g) => {
    const gk = gwKey(g);
    if (!nodes.has(gk)) return;
    const anchorKey = anchorKeyOfGateway(g);
    if (!anchorKey || !nodes.has(anchorKey)) return;
    const hasIncoming = edges.some(
      (e) => e.to === gk && e.preGateway && e.gatewayId === g.id,
    );
    if (!hasIncoming) {
      addEdge(anchorKey, gk, "", { preGateway: true, gatewayId: g.id });
    }
  });

  const list = Array.from(nodes.values());
  const maxCol = list.reduce((m, n) => Math.max(m, n.col), 0);
  const minLane = list.reduce((m, n) => Math.min(m, n.lane), 0);
  const maxLane = list.reduce((m, n) => Math.max(m, n.lane), 0);

  const positioned = list.map((n) => ({
    ...n,
    x: CANVAS_PAD_X + n.col * CANVAS_COL_GAP,
    y: CANVAS_PAD_Y + (n.lane - minLane) * CANVAS_LANE_GAP,
  }));

  return {
    nodes: positioned,
    edges,
    width: CANVAS_PAD_X * 2 + maxCol * CANVAS_COL_GAP,
    height: CANVAS_PAD_Y * 2 + (maxLane - minLane) * CANVAS_LANE_GAP,
  };
};

/**
 * Loop-back connector: the target sits at or behind the source's column, so a
 * straight line between their centers would run backwards through (and
 * overlap) whatever forward connector already occupies that gap — exactly the
 * "task -> gateway" connector when a branch loops back to the gateway's own
 * anchor task. Routed below the row instead, so it never shares a line with
 * the forward path: out the bottom of `from`, across under the row, up into
 * the bottom of `to`.
 */
const getLoopBackPath = (from, to) => {
  const fromBottom = from.y + halfHeightOf(from.kind);
  const toBottom = to.y + halfHeightOf(to.kind);
  const dropY =
    Math.max(fromBottom, toBottom) +
    28 +
    Math.min(60, Math.abs(from.col - to.col) * 8);
  const startX = from.x;
  const endX = to.x;
  const r = Math.min(12, Math.abs(endX - startX) / 2 || 12);
  const dir = endX < startX ? -1 : 1;
  const d = [
    `M ${startX} ${fromBottom}`,
    `L ${startX} ${dropY - r}`,
    `Q ${startX} ${dropY} ${startX + dir * r} ${dropY}`,
    `L ${endX - dir * r} ${dropY}`,
    `Q ${endX} ${dropY} ${endX} ${dropY - r}`,
    `L ${endX} ${toBottom}`,
  ].join(" ");
  return { d, midX: (startX + endX) / 2, midY: dropY };
};

/** Orthogonal elbow: out horizontally, one vertical step, in horizontally. */
const getElbowPath = (from, to, bow) => {
  if (to.x < from.x) return getLoopBackPath(from, to);

  const startX = from.x + halfWidthOf(from.kind) + 4;
  const endX = to.x - halfWidthOf(to.kind) - 8;
  const startY = from.y;
  const endY = to.y;

  if (Math.abs(startY - endY) < 0.5) {
    // A branch that skips straight past intervening trunk nodes on its own
    // row would otherwise draw a straight line coincident with — and hidden
    // behind — the trunk's own connectors. Route it through a corridor
    // clear of the row (not just a shallow bow at the midpoint, which still
    // cuts through boxes near either end) so the branch reads as its own
    // path for its *entire* length, not just at the middle.
    const r = 14;
    const preX = startX + 30;
    const postX = endX - 30;
    if (bow != null && postX - preX > 2 * r) {
      const down = bow > startY ? 1 : -1;
      const d = [
        `M ${startX} ${startY}`,
        `L ${preX - r} ${startY}`,
        `Q ${preX} ${startY} ${preX} ${startY + down * r}`,
        `L ${preX} ${bow - down * r}`,
        `Q ${preX} ${bow} ${preX + r} ${bow}`,
        `L ${postX - r} ${bow}`,
        `Q ${postX} ${bow} ${postX} ${bow - down * r}`,
        `L ${postX} ${startY + down * r}`,
        `Q ${postX} ${startY} ${postX + r} ${startY}`,
        `L ${endX} ${endY}`,
      ].join(" ");
      return { d, midX: (preX + postX) / 2, midY: bow };
    }
    return { d: `M ${startX} ${startY} L ${endX} ${endY}`, midX: (startX + endX) / 2, midY: startY };
  }

  const down = endY > startY ? 1 : -1;
  const trunkX = startX + Math.max(24, Math.min(70, (endX - startX) * 0.4));
  const r = Math.min(
    14,
    Math.abs(endY - startY) / 2,
    Math.max(0, trunkX - startX),
    Math.max(0, endX - trunkX),
  );
  const d = [
    `M ${startX} ${startY}`,
    `L ${trunkX - r} ${startY}`,
    `Q ${trunkX} ${startY} ${trunkX} ${startY + down * r}`,
    `L ${trunkX} ${endY - down * r}`,
    `Q ${trunkX} ${endY} ${trunkX + r} ${endY}`,
    `L ${endX} ${endY}`,
  ].join(" ");
  return { d, midX: trunkX, midY: (startY + endY) / 2 };
};

const ProcessBuilderPage = () => {
  const { setActiveSection } = useAppContext();

  const [companies, setCompanies] = React.useState([]);
  const [companyId, setCompanyId] = React.useState("");
  const [companyLoading, setCompanyLoading] = React.useState(true);

  const [processes, setProcesses] = React.useState([]);
  const [processId, setProcessId] = React.useState("");
  const [processesLoading, setProcessesLoading] = React.useState(false);

  const [processLoading, setProcessLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [creatingProcess, setCreatingProcess] = React.useState(false);
  const [newProcessOpen, setNewProcessOpen] = React.useState(false);
  const [processCategories, setProcessCategories] = React.useState([]);
  const [processStatuses, setProcessStatuses] = React.useState([]);
  const NEW_PROCESS_FORM_DEFAULTS = {
    process_name: "",
    process_code: "",
    version: 0,
    process_status_id: "",
    process_category_id: "",
    parent_process_id: "",
    process_overview: "",
  };
  const [newProcessForm, setNewProcessForm] = React.useState(
    NEW_PROCESS_FORM_DEFAULTS,
  );

  const [tasks, setTasks] = React.useState([]);
  const [form, setForm] = React.useState(null); // read-only carry-through fields for save
  const [workflow, setWorkflow] = React.useState([]);
  const [gateways, setGateways] = React.useState([]);
  const originalGatewayBackendIdsRef = React.useRef([]);
  const workflowRef = React.useRef([]);

  // Key of the "+" slot a drop would land on right now (see insertSlots).
  const [activeSlotKey, setActiveSlotKey] = React.useState(null);
  // { kind: "task" | "gateway" | "reorder", label, index? } for the thing
  // currently being dragged — drives the drop ghost on the path.
  const [dragItem, setDragItem] = React.useState(null);
  const [dragOverNodeIndex, setDragOverNodeIndex] = React.useState(null);
  const [dragOverCanvas, setDragOverCanvas] = React.useState(false);
  // id of the gateway diamond currently highlighted as a drop target
  const [dragOverGatewayId, setDragOverGatewayId] = React.useState(null);
  // index of the SVG edge currently highlighted as a drop target
  const [dragOverEdgeIndex, setDragOverEdgeIndex] = React.useState(null);
  // { fromIndex, toIndex, fromName, toName } while the swap confirm is open.
  const [swapPrompt, setSwapPrompt] = React.useState(null);
  const canvasInnerRef = React.useRef(null);
  const [taskSearch, setTaskSearch] = React.useState("");
  const [expandedGatewayId, setExpandedGatewayId] = React.useState(null);
  // The insert slot behind the "+" that was clicked; null = closed.
  const [addModal, setAddModal] = React.useState(null);
  const [modalSearch, setModalSearch] = React.useState("");
  // { id, tab } of the task whose details are open; null = closed.
  const [taskDetail, setTaskDetail] = React.useState(null);
  // Where a newly created task should land: a slot, or null for "just create
  // it and leave it in the panel". `false` = the create modal is closed.
  const [createTaskSlot, setCreateTaskSlot] = React.useState(false);

  React.useEffect(() => {
    workflowRef.current = workflow;
  }, [workflow]);

  React.useEffect(() => {
    (async () => {
      try {
        setCompanyLoading(true);
        const res = await getCompanyNames().catch(() => []);
        setCompanies(Array.isArray(res) ? res : []);
      } finally {
        setCompanyLoading(false);
      }
    })();
    (async () => {
      const [catsRes, statusRes] = await Promise.all([
        getProcessCategories().catch(() => []),
        getProcessStatuses().catch(() => []),
      ]);
      setProcessCategories(Array.isArray(catsRes) ? catsRes : []);
      setProcessStatuses(Array.isArray(statusRes) ? statusRes : []);
    })();
  }, []);

  const resetProcessState = () => {
    setProcessId("");
    setForm(null);
    setWorkflow([]);
    setGateways([]);
    originalGatewayBackendIdsRef.current = [];
  };

  const onCompanyChange = async (val) => {
    setCompanyId(val);
    resetProcessState();
    setNewProcessOpen(false);
    setProcesses([]);
    setTasks([]);
    if (!val) return;
    setProcessesLoading(true);
    try {
      const [procRes, taskRes] = await Promise.all([
        getProcessesByCompanyId(val).catch(() => []),
        getTasksByCompanyId(val).catch(() => []),
      ]);
      setProcesses(Array.isArray(procRes) ? procRes : []);
      setTasks(Array.isArray(taskRes) ? taskRes : []);
    } finally {
      setProcessesLoading(false);
    }
  };

  const onProcessChange = async (val) => {
    resetProcessState();
    setProcessId(val);
    if (!val) return;
    setProcessLoading(true);
    try {
      const proc = await getProcessWithRelations(val);
      setForm({
        process_name: proc.process_name || "",
        process_code: proc.process_code || "",
        version: proc.process_version ?? proc.version ?? 0,
        company_id: proc.company_id || proc.company?.company_id || "",
        process_status_id:
          proc.process_status_id ||
          proc.process_status?.id ||
          proc.processStatus?.id ||
          proc.status_id ||
          "",
        process_category_id:
          proc.process_category_id ||
          proc.process_category?.id ||
          proc.processCategory?.id ||
          proc.category_id ||
          "",
        parent_process_id: proc.parent_process_id || "",
        process_overview: proc.process_overview || "",
        case_arrival_rate:
          proc.case_arrival_rate != null ? String(proc.case_arrival_rate) : "",
      });

      const rows = Array.isArray(proc.process_tasks)
        ? [...proc.process_tasks]
        : [];
      rows.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
      setWorkflow(
        rows.map((pt) =>
          pt.child_process_id
            ? {
                isChildProcess: true,
                child_process_id: pt.child_process_id || "",
                task_id: "",
                job_id: "",
              }
            : {
                task_id: pt.task_id || pt.task?.task_id || "",
                job_id:
                  (pt.task?.jobTasks && pt.task.jobTasks[0]?.job?.job_id) ||
                  pt.job_id ||
                  "",
              },
        ),
      );

      const gws = Array.isArray(proc.gateways)
        ? proc.gateways.map((g) => {
            const allBranches = Array.isArray(g.branches) ? g.branches : [];
            const firstVal = (...vals) => {
              for (const v of vals) {
                if (v != null && String(v) !== "") return String(v);
              }
              return "";
            };
            const isEndpoint = (b) => {
              const target = firstVal(b.targetTaskId, b.target_task_id);
              const endName = (b.endEventName || b.end_event_name || "").trim();
              const endTask = firstVal(b.endTaskId, b.end_task_id);
              return target === "" && endTask !== "" && endName === "";
            };

            const regularBranches = allBranches
              .filter((b) => !isEndpoint(b))
              .map((b, idx) => ({
                id: `${g.name || "gw"}-b-${idx}-${Math.random()
                  .toString(16)
                  .slice(2)}`,
                _backendId: b.branch_pk_id ?? b.id ?? null,
                targetTaskId: firstVal(b.targetTaskId, b.target_task_id),
                _targetGatewayPk:
                  b.target_gateway_id ?? b.targetGatewayId ?? null,
                endEventName: b.endEventName || b.end_event_name || "",
                condition: b.condition || "",
                isDefault: !!(b.isDefault ?? b.is_default),
                connectToEnd: (b.connectToEnd ?? b.connect_to_end) !== false,
              }));

            const endpoints = allBranches
              .filter(isEndpoint)
              .map((e, idx) => ({
                id: `${g.name || "gw"}-e-${idx}-${Math.random()
                  .toString(16)
                  .slice(2)}`,
                _backendId: e.branch_pk_id ?? e.id ?? null,
                endTaskId: firstVal(e.endTaskId, e.end_task_id),
                endEventName: e.endEventName || e.end_event_name || "",
                condition: e.condition || "",
              }));

            return {
              id: `${g.name || g.type || "gw"}-${Math.random()
                .toString(16)
                .slice(2)}`,
              _backendId: g.gateway_pk_id ?? g.id ?? null,
              type: String(g.type || g.gateway_type || "EXCLUSIVE").toUpperCase(),
              name: g.name || "",
              afterTaskId:
                g.afterTaskId != null
                  ? String(g.afterTaskId)
                  : g.after_task_id != null
                    ? String(g.after_task_id)
                    : START_EVENT_GATEWAY_ID,
              convergeAtTaskId: (() => {
                const raw =
                  g.convergeAtTaskId != null
                    ? g.convergeAtTaskId
                    : g.converge_at_task_id != null
                      ? g.converge_at_task_id
                      : "";
                if (raw === "" || raw == null || Number(raw) === 0) return "";
                return String(raw);
              })(),
              convergeGatewayName:
                g.convergeGatewayName || g.converge_gateway_name || "",
              convergeToEnd: !!(g.convergeToEnd ?? g.converge_to_end),
              _convergeAtGatewayPk:
                g.convergeAtGatewayId ?? g.converge_at_gateway_id ?? null,
              convergeAtGatewayId: "",
              branches:
                regularBranches.length > 0 || endpoints.length > 0
                  ? regularBranches
                  : [
                      { id: `${Math.random()}`, targetTaskId: "", condition: "" },
                      { id: `${Math.random()}`, targetTaskId: "", condition: "" },
                    ],
              endpoints: endpoints,
            };
          })
        : [];

      for (const gw of gws) {
        if (gw._convergeAtGatewayPk != null) {
          const target = gws.find(
            (x) => String(x._backendId) === String(gw._convergeAtGatewayPk),
          );
          if (target) gw.convergeAtGatewayId = target.id;
        }
        for (const br of gw.branches || []) {
          if (br._targetGatewayPk != null) {
            const target = gws.find(
              (x) => String(x._backendId) === String(br._targetGatewayPk),
            );
            if (target) br.targetTaskId = `__GW__${target.id}`;
          }
        }
      }
      setGateways(gws);
      originalGatewayBackendIdsRef.current = gws
        .map((g) => g._backendId)
        .filter(Boolean);
    } catch (e) {
      toast.error(e?.message || "Failed to load process");
      resetProcessState();
    } finally {
      setProcessLoading(false);
    }
  };

  const updateNewProcessForm = (key, value) => {
    setNewProcessForm((prev) => ({ ...prev, [key]: value }));
  };

  const openNewProcessForm = () => {
    if (!companyId) {
      toast.error("Select a company before creating a process.");
      return;
    }
    setNewProcessOpen(true);
  };

  const cancelNewProcess = () => {
    if (creatingProcess) return;
    setNewProcessOpen(false);
    setNewProcessForm(NEW_PROCESS_FORM_DEFAULTS);
  };

  const createAndOpenProcess = async () => {
    if (!companyId) {
      toast.error("Company is required");
      return;
    }
    const processName = newProcessForm.process_name.trim();
    const processCode = newProcessForm.process_code.trim();
    if (!processName) {
      toast.error("Process Name is required");
      return;
    }
    if (!processCode) {
      toast.error("Process Code is required");
      return;
    }

    const overview =
      newProcessForm.process_overview.trim() ||
      "Workflow draft created from Process Builder.";

    try {
      setCreatingProcess(true);
      const payload = {
        process_name: processName,
        process_code: processCode,
        version:
          newProcessForm.version === "" ? 0 : Number(newProcessForm.version),
        company_id: Number(companyId),
        process_status_id:
          newProcessForm.process_status_id ?
            Number(newProcessForm.process_status_id)
          : null,
        process_category_id:
          newProcessForm.process_category_id ?
            Number(newProcessForm.process_category_id)
          : null,
        parent_process_id:
          newProcessForm.parent_process_id ?
            Number(newProcessForm.parent_process_id)
          : null,
        process_overview: overview,
        workflow: [],
        gateways: [],
      };
      const created = await createProcess(payload, { suppressErrorPage: true });
      const refreshed = await getProcessesByCompanyId(companyId).catch(() => []);
      const nextProcesses = Array.isArray(refreshed) ? refreshed : [];
      setProcesses(nextProcesses);

      const createdId =
        extractCreatedProcessId(created) ||
        nextProcesses.find(
          (p) =>
            String(p.process_code || p.code || "").trim() === processCode ||
            String(p.process_name || p.name || "").trim() === processName,
        )?.process_id ||
        nextProcesses.find(
          (p) =>
            String(p.process_code || p.code || "").trim() === processCode ||
            String(p.process_name || p.name || "").trim() === processName,
        )?.id;

      try {
        localStorage.setItem("processesNeedsRefresh", "1");
      } catch {}

      setNewProcessOpen(false);
      setNewProcessForm(NEW_PROCESS_FORM_DEFAULTS);

      if (createdId) {
        await onProcessChange(String(createdId));
        toast.success(`Process "${processName}" created and opened.`);
      } else {
        toast.success(`Process "${processName}" created. Select it to open the canvas.`);
      }
    } catch (e) {
      toast.error(e?.message || "Failed to create process");
    } finally {
      setCreatingProcess(false);
    }
  };

  // --- Attach / reorder / detach via drag-and-drop ---

  const handleAttachTask = async (taskId, insertBeforeIndex = null) => {
    const idNum = Number(taskId);
    if (!Number.isFinite(idNum) || idNum <= 0) return;
    if (
      workflowRef.current.some(
        (r) => !r.isChildProcess && Number(r.task_id) === idNum,
      )
    ) {
      return;
    }

    const newRow = { task_id: idNum, job_id: "" };
    setWorkflow((prev) => {
      if (insertBeforeIndex == null || insertBeforeIndex >= prev.length) {
        return [...prev, newRow];
      }
      const copy = [...prev];
      copy.splice(insertBeforeIndex, 0, newRow);
      return copy;
    });

    const cached = tasks.find((t) => Number(t.task_id || t.id) === idNum);
    let jobsForTask = mapJobTasksToWorkflowJobs(cached?.jobTasks);
    let firstJobId = jobsForTask[0]?.job_id ?? null;
    if (!jobsForTask.length) {
      try {
        const rel = await getTaskWithRelations(idNum);
        jobsForTask = mapJobTasksToWorkflowJobs(rel?.jobTasks);
        firstJobId = jobsForTask[0]?.job_id ?? null;
      } catch {}
    }
    setWorkflow((prev) =>
      prev.map((r) =>
        !r.isChildProcess && Number(r.task_id) === idNum
          ? { ...r, job_id: firstJobId ?? r.job_id }
          : r,
      ),
    );
  };

  const handleReorder = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || fromIndex == null || toIndex == null) return;
    const adjustedTo = fromIndex < toIndex ? toIndex - 1 : toIndex;
    setWorkflow((prev) => {
      const copy = [...prev];
      const [moved] = copy.splice(fromIndex, 1);
      copy.splice(adjustedTo, 0, moved);
      return copy;
    });
  };

  /**
   * Trade two steps' places.
   *
   * Gateway anchors, branch targets and join points address steps by id, but
   * the layout hands each branch a *range* of array positions. Moving the rows
   * alone therefore drags all that wiring to new positions, and any step that
   * ends up outside every branch's range stops being reachable — which is why
   * swapping used to strand steps in the corner of the canvas instead of
   * swapping them.
   *
   * Swapping the two ids everywhere alongside the rows keeps every reference on
   * the position it already held. The graph is then provably identical: only
   * these two boxes trade contents, and nothing can come loose.
   */
  const swapWorkflowRows = (a, b) => {
    if (a == null || b == null || a === b) return;
    if (a < 0 || b < 0 || a >= workflow.length || b >= workflow.length) return;

    const idA = String(stepIdOfRow(workflow[a]) ?? "");
    const idB = String(stepIdOfRow(workflow[b]) ?? "");

    setWorkflow((prev) => {
      if (a >= prev.length || b >= prev.length) return prev;
      const copy = [...prev];
      copy[a] = prev[b];
      copy[b] = prev[a];
      return copy;
    });

    if (!idA || !idB || idA === idB) return;
    const swapId = (value) => {
      if (value == null || value === "") return value;
      const s = String(value);
      if (s === idA) return idB;
      if (s === idB) return idA;
      return value;
    };
    setGateways((prev) =>
      prev.map((g) => ({
        ...g,
        afterTaskId:
          String(g.afterTaskId) === START_EVENT_GATEWAY_ID
            ? g.afterTaskId
            : swapId(g.afterTaskId),
        convergeAtTaskId: swapId(g.convergeAtTaskId),
        branches: (g.branches || []).map((br) =>
          // "__GW__<id>" targets another gateway, not a step — leave it alone.
          typeof br.targetTaskId === "string" &&
          br.targetTaskId.startsWith("__GW__")
            ? br
            : { ...br, targetTaskId: swapId(br.targetTaskId) },
        ),
        endpoints: (g.endpoints || []).map((ep) => ({
          ...ep,
          endTaskId: swapId(ep.endTaskId),
        })),
      })),
    );
  };

  const removeWorkflowRow = (index) => {
    const removedId = String(stepIdOfRow(workflow[index]) ?? "");
    setWorkflow((prev) => prev.filter((_, i) => i !== index));

    if (!removedId) return;
    // Deleting a task strands any gateway anchored to it and any branch/join
    // that pointed at it. Cascade-delete gateways whose anchor task is gone
    // (they'd otherwise render as unreachable orphans, see BUG-06), and clear
    // dangling references from the ones that survive so they can be
    // reconfigured instead of silently pointing at a task that no longer exists.
    setGateways((prev) =>
      prev
        .filter((g) => String(g.afterTaskId ?? "") !== removedId)
        .map((g) => ({
          ...g,
          convergeAtTaskId:
            String(g.convergeAtTaskId ?? "") === removedId
              ? null
              : g.convergeAtTaskId,
          branches: (g.branches || []).map((br) =>
            String(br.targetTaskId ?? "") === removedId
              ? { ...br, targetTaskId: null }
              : br,
          ),
          endpoints: (g.endpoints || []).map((ep) =>
            String(ep.endTaskId ?? "") === removedId
              ? { ...ep, endTaskId: null }
              : ep,
          ),
        })),
    );
  };

  // A task edited in the detail modal can have been renamed and can have had
  // its RACI reshuffled — refresh the label source and drop a workflow job_id
  // that the task no longer carries, or the workflow would save a stale link.
  const handleTaskSaved = (updated) => {
    if (!updated) return;
    const id = String(updated.task_id ?? taskDetail?.id ?? "");
    if (!id) return;

    setTasks((prev) =>
      prev.map((t) =>
        String(t.task_id || t.id) === id ? { ...t, ...updated } : t,
      ),
    );

    const jobs = mapJobTasksToWorkflowJobs(updated.jobTasks);
    setWorkflow((prev) =>
      prev.map((r) => {
        if (r.isChildProcess || String(r.task_id) !== id) return r;
        if (jobs.some((j) => String(j.job_id) === String(r.job_id))) return r;
        return { ...r, job_id: jobs[0]?.job_id ?? "" };
      }),
    );
  };

  /**
   * A task created from inside the builder: pull the refreshed company list so
   * the new task is selectable, drop it on the slot the user started from, then
   * open its RACI — a task with no job assigned blocks the workflow save, so
   * leaving the user on the diagram would be a dead end.
   */
  const handleTaskCreated = async (created) => {
    const slot = createTaskSlot;
    setCreateTaskSlot(false);
    if (!created || !companyId) return;

    const refreshed = await getTasksByCompanyId(companyId).catch(() => null);
    const list = Array.isArray(refreshed) ? refreshed : tasks;
    if (Array.isArray(refreshed)) setTasks(refreshed);

    // The create response's shape varies, so fall back to matching on the code
    // we just submitted, which the list definitely carries.
    let newId = created.task_id;
    if (newId == null) {
      const match = list.find(
        (t) =>
          String(t.task_code || t.code || "") === String(created.task_code),
      );
      newId = match?.task_id ?? match?.id ?? null;
    }
    if (newId == null) {
      toast.error(
        "Task created, but it couldn't be placed automatically — find it in the Company Tasks panel.",
      );
      return;
    }

    if (slot !== null) attachTaskAtSlot(Number(newId), slot);
    setTaskDetail({ id: newId, tab: "raci" });
  };

  const workflowTaskOptions = React.useMemo(() => {
    return workflow
      .map((row, idx) => {
        if (row.isChildProcess) {
          const id = row.child_process_id;
          if (!id) return null;
          const cp = processes.find(
            (p) => String(p.process_id || p.id) === String(id),
          );
          const name = cp?.process_name || cp?.name;
          const code = cp?.process_code || cp?.code;
          const base = name
            ? `${name}${code ? ` [${code}]` : ""}`
            : `Process #${id}`;
          return {
            id: String(id),
            label: `${base} (Subprocess)`,
            order: idx + 1,
            isSubProcess: true,
          };
        }
        const id = row.task_id;
        if (!id) return null;
        const t = tasks.find((x) => String(x.task_id || x.id) === String(id));
        const label = t
          ? `${t.task_name || t.name}${
              t.task_code || t.code ? ` [${t.task_code || t.code}]` : ""
            }`
          : `Task #${id}`;
        return { id: String(id), label, order: idx + 1 };
      })
      .filter(Boolean);
  }, [workflow, tasks, processes]);

  // --- Gateway CRUD + drag-and-drop wiring ---------------------------------
  // Placement (afterTaskId) is set purely by which connector a gateway-type
  // chip is dropped on — no dropdown. Branch/converge targets are set by
  // dropping a task (dragged off its box in the canvas), another gateway (its
  // diamond), or the End marker (the ■ node) onto a slot — no dropdown either.

  const updateGateway = (gatewayId, updater) => {
    setGateways((prev) =>
      prev.map((g) => (g.id === gatewayId ? { ...g, ...updater(g) } : g)),
    );
  };

  const addGatewayAt = (afterId, type) => {
    // Placement is keyed by afterTaskId, so a second gateway on the same
    // anchor would never be reachable on the path — say so instead of
    // dropping an orphan onto the canvas.
    if (gateways.some((g) => String(g.afterTaskId) === String(afterId))) {
      toast.error(
        `A gateway is already attached after ${getPlacementLabel(afterId)}.`,
      );
      return;
    }
    const newGw = {
      id: createGatewayId(),
      _backendId: null,
      type: normalizeGatewayType(type) || "EXCLUSIVE",
      name: "",
      afterTaskId: String(afterId),
      convergeAtTaskId: "",
      convergeGatewayName: "",
      convergeToEnd: false,
      convergeAtGatewayId: "",
      branches: [
        {
          id: createBranchId(),
          targetTaskId: "",
          condition: "",
          endEventName: "",
          isDefault: false,
          connectToEnd: true,
        },
        {
          id: createBranchId(),
          targetTaskId: "",
          condition: "",
          endEventName: "",
          isDefault: false,
          connectToEnd: true,
        },
      ],
      endpoints: [],
    };
    setGateways((prev) => [...prev, newGw]);
    setExpandedGatewayId(newGw.id);
  };

  const removeGateway = (gatewayId) => {
    setGateways((prev) => prev.filter((g) => g.id !== gatewayId));
    setExpandedGatewayId((id) => (id === gatewayId ? null : id));
  };

  const addBranch = (gatewayId) => {
    updateGateway(gatewayId, (g) => ({
      branches: [
        ...g.branches,
        {
          id: createBranchId(),
          targetTaskId: "",
          condition: "",
          endEventName: "",
          isDefault: false,
          connectToEnd: true,
        },
      ],
    }));
  };

  const removeBranch = (gatewayId, branchId) => {
    setGateways((prev) =>
      prev.map((g) => {
        if (g.id !== gatewayId) return g;
        if (g.branches.length <= 2) return g;
        return { ...g, branches: g.branches.filter((b) => b.id !== branchId) };
      }),
    );
  };

  const updateBranchField = (gatewayId, branchId, field, value) => {
    updateGateway(gatewayId, (g) => ({
      branches: g.branches.map((b) =>
        b.id === branchId ? { ...b, [field]: value } : b,
      ),
    }));
  };

  // Branch / converge targets are picked by clicking a chip in the gateway
  // modal. `value` is the same shape the save payload expects: "" = End,
  // "__GW__<id>" = another gateway, otherwise a task/subprocess id.
  const setBranchTarget = (gatewayId, branchId, value) => {
    updateGateway(gatewayId, (g) => ({
      branches: g.branches.map((b) =>
        b.id === branchId
          ? {
              ...b,
              targetTaskId: value,
              endEventName: value ? "" : b.endEventName,
            }
          : b,
      ),
    }));
  };

  const setConvergeTarget = (gatewayId, value) => {
    if (value === "__END__") {
      updateGateway(gatewayId, () => ({
        convergeToEnd: true,
        convergeAtTaskId: "",
        convergeAtGatewayId: "",
      }));
    } else if (typeof value === "string" && value.startsWith("__GW__")) {
      updateGateway(gatewayId, () => ({
        convergeAtGatewayId: value.slice(6),
        convergeToEnd: false,
        convergeAtTaskId: "",
      }));
    } else {
      updateGateway(gatewayId, () => ({
        convergeAtTaskId: value,
        convergeToEnd: false,
        convergeAtGatewayId: "",
      }));
    }
  };

  const getSlotLabel = (targetTaskId, endEventName) => {
    if (!targetTaskId) return endEventName ? `End: ${endEventName}` : "End (shared)";
    if (typeof targetTaskId === "string" && targetTaskId.startsWith("__GW__")) {
      const ref = targetTaskId.slice(6);
      const g = gateways.find((x) => String(x.id) === ref);
      return g ? `↳ ${g.name || getGatewayTypeInfo(g.type).label}` : "↳ Gateway";
    }
    const opt = workflowTaskOptions.find((o) => String(o.id) === String(targetTaskId));
    return opt ? opt.label : `#${targetTaskId}`;
  };

  const getPlacementLabel = (afterTaskId) => {
    if (String(afterTaskId) === START_EVENT_GATEWAY_ID) return "Start";
    const opt = workflowTaskOptions.find(
      (o) => String(o.id) === String(afterTaskId),
    );
    return opt ? opt.label : `#${afterTaskId}`;
  };

  const getConvergeSlotLabel = (g) => {
    if (g.convergeToEnd) return "End (join, then end)";
    if (g.convergeAtGatewayId) {
      const target = gateways.find((x) => String(x.id) === String(g.convergeAtGatewayId));
      return target ? `↳ ${target.name || getGatewayTypeInfo(target.type).label}` : "↳ Gateway";
    }
    if (g.convergeAtTaskId) {
      const opt = workflowTaskOptions.find((o) => String(o.id) === String(g.convergeAtTaskId));
      return opt ? opt.label : `#${g.convergeAtTaskId}`;
    }
    return "Not set";
  };

  // Best-guess join point for a gateway that needs one but doesn't have it
  // set yet: the task immediately after the furthest-along branch target, so
  // every branch (including shorter ones) has somewhere to land once it's
  // done. Falls back to End when no branch continues onto the workflow (e.g.
  // every branch already points at another gateway or nothing is set yet).
  const suggestConvergeTarget = (g) => {
    const continuingOrders = (g.branches || [])
      .map((b) => b.targetTaskId)
      .filter((t) => t && !(typeof t === "string" && t.startsWith("__GW__")))
      .map(
        (t) =>
          workflowTaskOptions.find((o) => String(o.id) === String(t))?.order,
      )
      .filter((o) => Number.isFinite(o));
    if (continuingOrders.length === 0) return "__END__";
    const maxOrder = Math.max(...continuingOrders);
    const next = workflowTaskOptions.find((o) => o.order === maxOrder + 1);
    return next ? String(next.id) : "__END__";
  };

  const attachedTaskIds = React.useMemo(
    () =>
      new Set(
        workflow
          .filter((r) => !r.isChildProcess && r.task_id)
          .map((r) => String(r.task_id)),
      ),
    [workflow],
  );

  const selectedCompanyName = React.useMemo(() => {
    const c = companies.find(
      (x) => String(x.company_id ?? x.id) === String(companyId),
    );
    return c?.name || "";
  }, [companies, companyId]);

  const availableTasks = React.useMemo(() => {
    const q = taskSearch.trim().toLowerCase();
    return tasks
      .filter((t) => !attachedTaskIds.has(String(t.task_id || t.id)))
      .filter((t) => {
        if (!q) return true;
        const name = String(t.task_name || t.name || "").toLowerCase();
        const code = String(t.task_code || t.code || "").toLowerCase();
        return name.includes(q) || code.includes(q);
      });
  }, [tasks, attachedTaskIds, taskSearch]);

  const validate = () => {
    if (!form) return "Select a process first";
    if (!Array.isArray(workflow) || workflow.length === 0)
      return "Attach at least one task to the workflow";
    for (const [i, r] of workflow.entries()) {
      if (r.isChildProcess) {
        if (!r.child_process_id)
          return `Row ${i + 1}: Child process is missing`;
      } else {
        if (!r.task_id) return `Row ${i + 1}: Task is missing`;
        if (!r.job_id)
          return `Row ${i + 1}: Assign a RACI job to this task before saving — double-click the task on the diagram, then use its RACI tab`;
      }
    }
    for (const [i, g] of gateways.entries()) {
      const type = normalizeGatewayType(g.type);
      const isParallel = type === "PARALLEL";
      const totalBranches = Array.isArray(g.branches) ? g.branches.length : 0;
      const totalEndpoints = Array.isArray(g.endpoints) ? g.endpoints.length : 0;
      const totalOutgoing = totalBranches + totalEndpoints;

      if (!g.afterTaskId) return `Gateway ${i + 1}: After Task is required`;
      if (type === "EXCLUSIVE" && totalOutgoing < 2)
        return `Gateway ${i + 1}: Exclusive gateways require at least two outgoing paths`;
      if (isParallel) {
        if (totalBranches < 2)
          return `Gateway ${i + 1}: Parallel gateways require at least two branches`;
        if (totalEndpoints > 0)
          return `Gateway ${i + 1}: Parallel gateways cannot have end events`;
      }
      if (requiresConvergence(g) && !hasConvergeTarget(g))
        return `Gateway ${i + 1}: Join point is required (a task, End event, or another gateway)`;
      if (!Array.isArray(g.branches) || g.branches.length === 0)
        return `Gateway ${i + 1}: At least one branch is required`;
      const missingTargets = (g.branches || []).filter((b) => !b.targetTaskId);
      if (isParallel && missingTargets.length > 0) {
        return `Gateway ${i + 1}, Branch ${
          (g.branches || []).indexOf(missingTargets[0]) + 1
        }: Target task is required for parallel gateways`;
      }

      // Two branches pointing at the same task/gateway is conflicting,
      // redundant routing — every outgoing path must go somewhere distinct.
      // A blank target (shared End event) is exempt.
      const targetCounts = new Map();
      (g.branches || []).forEach((b) => {
        if (!b.targetTaskId) return;
        const key = String(b.targetTaskId);
        targetCounts.set(key, (targetCounts.get(key) || 0) + 1);
      });
      const dupeIdx = (g.branches || []).findIndex(
        (b) => b.targetTaskId && targetCounts.get(String(b.targetTaskId)) > 1,
      );
      if (dupeIdx >= 0) {
        return `Gateway ${i + 1}, Branch ${
          dupeIdx + 1
        }: Another branch already routes to this same target`;
      }
    }
    return "";
  };

  const selectedGateway =
    gateways.find((g) => g.id === expandedGatewayId) || null;

  const closeGatewayPanel = React.useCallback((e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setExpandedGatewayId(null);
  }, []);

  const canvas = React.useMemo(() => {
    const labelOf = (row) => {
      if (row.isChildProcess) {
        const cp = processes.find(
          (p) => String(p.process_id || p.id) === String(row.child_process_id),
        );
        return cp
          ? `${cp.process_name || cp.name} (Subprocess)`
          : `Process #${row.child_process_id}`;
      }
      const t = tasks.find(
        (x) => String(x.task_id || x.id) === String(row.task_id),
      );
      return t ? t.task_name || t.name : `Task #${row.task_id}`;
    };
    return buildCanvasModel(workflow, gateways, labelOf);
  }, [workflow, gateways, tasks, processes]);

  const canvasNodeByKey = React.useMemo(
    () => new Map(canvas.nodes.map((n) => [n.key, n])),
    [canvas.nodes],
  );

  const orphanCount = React.useMemo(
    () => canvas.nodes.filter((n) => n.orphan).length,
    [canvas.nodes],
  );

  // --- Where a drop on the path lands -------------------------------------
  // Every connector is an insertion slot: the "+" is just its visible handle.
  // Dropping anywhere on the canvas resolves to one of these, so the user
  // never has to hit the 26px circle exactly. Three flavours:
  //
  //   spine          — plain array splice at `insertIndex`.
  //   before-gateway — splice, then re-anchor the gateway onto the new step so
  //                    it stays after it instead of jumping in front.
  //   branch         — splice, then re-point that one branch at the new step so
  //                    the gateway reaches it (branches address steps by id).
  //
  // Without the last two, anything dropped next to a gateway landed in the
  // array but was never reachable from the diagram, so it fell out to the
  // "unreached node" corner of the canvas.

  const indexOfStepId = React.useCallback(
    (id) =>
      workflow.findIndex((r) => String(stepIdOfRow(r)) === String(id)),
    [workflow],
  );

  // Index of the step a gateway hangs off; -1 when it sits on the Start event.
  const anchorIndexOfGateway = React.useCallback(
    (gw) => {
      if (!gw) return null;
      if (String(gw.afterTaskId) === START_EVENT_GATEWAY_ID) return -1;
      const idx = indexOfStepId(gw.afterTaskId);
      return idx < 0 ? null : idx;
    },
    [indexOfStepId],
  );

  const insertSlots = React.useMemo(() => {
    const out = [];
    const seen = new Set();
    // Several branches can end on the same node (typically End); fan their
    // handles out horizontally instead of stacking them on one pixel.
    const usedPoints = new Map();

    const nudge = (x, y, dir) => {
      const cell = `${Math.round(x)}:${Math.round(y)}`;
      const taken = usedPoints.get(cell) || 0;
      usedPoints.set(cell, taken + 1);
      return { x: x + dir * taken * 34, y };
    };

    // Sits on the connector's final horizontal run, just before the arrowhead.
    const pointNearTarget = (from, to) => {
      const startX = from.x + halfWidthOf(from.kind) + 4;
      const endX = to.x - halfWidthOf(to.kind) - 8;
      return nudge(Math.max(startX + 15, endX - 16), to.y, -1);
    };

    // Sits on the connector's first horizontal run, just after the source box.
    const pointNearSource = (from) =>
      nudge(from.x + halfWidthOf(from.kind) + 22, from.y, 1);

    const gatewayById = new Map(gateways.map((g) => [g.id, g]));

    canvas.edges.forEach((edge, ei) => {
      const from = canvasNodeByKey.get(edge.from);
      const to = canvasNodeByKey.get(edge.to);
      if (!from || !to) return;

      if (edge.spine && edge.insertIndex != null) {
        // A connector that changes lane is a branch lane rejoining the spine
        // (or running into End). Its edge.insertIndex points past every other
        // branch, so append to the branch that owns this tail instead.
        const rejoining = Math.abs(from.y - to.y) > 0.5;
        const insertIndex = rejoining ? from.chainEndIdx : edge.insertIndex;
        if (insertIndex == null) return;
        const key = `s-${insertIndex}-${edge.from}`;
        if (seen.has(key)) return;
        seen.add(key);
        out.push({
          key,
          kind: "spine",
          insertIndex,
          edgeIndex: ei,
          ...(rejoining
            ? pointNearSource(from)
            : { x: (from.x + to.x) / 2, y: from.y }),
        });
        return;
      }

      if (edge.preGateway && edge.gatewayId) {
        const gw = gatewayById.get(edge.gatewayId);
        const anchorIdx = anchorIndexOfGateway(gw);
        if (anchorIdx == null) return;
        const key = `pg-${edge.gatewayId}`;
        if (seen.has(key)) return;
        seen.add(key);
        out.push({
          key,
          kind: "before-gateway",
          gatewayId: edge.gatewayId,
          insertIndex: anchorIdx + 1,
          edgeIndex: ei,
          ...pointNearTarget(from, to),
        });
        return;
      }

      // Loop-backs are excluded: "insert on this edge" has no sane meaning
      // when the branch jumps backwards over steps it already passed.
      if (edge.branch && edge.fromGatewayId && edge.branchId && !edge.backward) {
        const gw = gatewayById.get(edge.fromGatewayId);
        const anchorIdx = anchorIndexOfGateway(gw);
        if (anchorIdx == null) return;

        let insertIndex;
        if (to.kind === "task" || to.kind === "subprocess") {
          if (to.trunkIndex == null) return;
          insertIndex = to.trunkIndex;
        } else if (to.kind === "end") {
          insertIndex = workflow.length;
        } else {
          // Gateway → gateway: there is no step to splice between them.
          return;
        }
        if (insertIndex <= anchorIdx) return;

        const key = `br-${edge.fromGatewayId}-${edge.branchId}`;
        if (seen.has(key)) return;
        seen.add(key);
        out.push({
          key,
          kind: "branch",
          gatewayId: edge.fromGatewayId,
          branchId: edge.branchId,
          branchKind: edge.branchKind,
          insertIndex,
          edgeIndex: ei,
          ...pointNearTarget(from, to),
        });
      }
    });
    return out;
  }, [
    canvas.edges,
    canvasNodeByKey,
    gateways,
    workflow,
    anchorIndexOfGateway,
  ]);

  const activeSlot =
    insertSlots.find((s) => s.key === activeSlotKey) || null;

  const slotByEdgeIndex = React.useMemo(() => {
    const m = new Map();
    insertSlots.forEach((s) => {
      if (s.edgeIndex != null && !m.has(s.edgeIndex)) m.set(s.edgeIndex, s);
    });
    return m;
  }, [insertSlots]);

  const branchOfSlot = (gw, slot) => {
    if (!gw || !slot) return null;
    const list =
      slot.branchKind === "endpoint" ? gw.endpoints || [] : gw.branches || [];
    const item = list.find((x) => x.id === slot.branchId) || null;
    return item ? { item, list } : null;
  };

  // Human-readable "where this drop will land", used by the "+" tooltips and
  // the add-to-path modal title.
  const describeSlot = (slot) => {
    if (!slot) return "at the end of the path";
    if (slot.kind === "before-gateway") {
      const gw = gateways.find((g) => g.id === slot.gatewayId);
      const info = getGatewayTypeInfo(gw?.type);
      return `before ${gw?.name || info.label} ${info.symbol}`;
    }
    if (slot.kind === "branch") {
      const gw = gateways.find((g) => g.id === slot.gatewayId);
      const info = getGatewayTypeInfo(gw?.type);
      const found = branchOfSlot(gw, slot);
      let label = "";
      if (found) {
        const { item, list } = found;
        label =
          item.condition || item.endEventName ||
          `path ${list.indexOf(item) + 1}`;
      }
      return `on the "${label || "branch"}" path of ${
        gw?.name || info.label
      } ${info.symbol}`;
    }
    return `after ${getPlacementLabel(afterIdForIndex(slot.insertIndex))}`;
  };

  // A step dropped at slot `insertIndex` splices in right after whatever the
  // array holds at insertIndex - 1 — that predecessor is also the anchor a
  // gateway's afterTaskId needs.
  const afterIdForIndex = (insertIndex) => {
    const prev = workflow[insertIndex - 1];
    if (!prev) return START_EVENT_GATEWAY_ID;
    return prev.isChildProcess ? prev.child_process_id : prev.task_id;
  };

  const trunkNodeName = (trunkIndex) =>
    canvas.nodes.find((n) => n.trunkIndex === trunkIndex)?.name ||
    `Step ${trunkIndex + 1}`;

  // getData() is blocked during dragover, so intent has to come from the
  // advertised types.
  const pathDragKind = (e) => {
    const types = Array.from(e.dataTransfer.types || []);
    if (types.includes("text/x-gateway-type")) return "gateway";
    if (types.includes("text/x-gateway-ref")) return "gateway-reposition";
    if (types.includes("text/x-task-id")) return "task";
    if (types.includes("text/x-row-index")) return "reorder";
    return null;
  };

  const canvasPointOf = (e) => {
    const el = canvasInnerRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  /**
   * Resolves a pointer position to the slot a drop should use. Landing on a
   * box or a diamond means "put it after this one", so its outgoing slot wins;
   * otherwise the nearest slot does, with vertical distance weighted heavily
   * so a drop never jumps to a parallel branch that only looks close.
   */
  const slotForEvent = (e) => {
    if (!insertSlots.length) return null;
    const p = canvasPointOf(e);
    if (!p) return null;

    const hit = canvas.nodes.find(
      (n) =>
        Math.abs(n.x - p.x) <= halfWidthOf(n.kind) &&
        Math.abs(n.y - p.y) <= halfHeightOf(n.kind),
    );
    if (hit) {
      const outgoing = insertSlots
        .filter((s) => s.x > hit.x && Math.abs(s.y - hit.y) < 1)
        .sort((a, b) => a.x - b.x)[0];
      if (outgoing) return outgoing;
    }

    let best = null;
    let bestDist = Infinity;
    insertSlots.forEach((s) => {
      const dx = s.x - p.x;
      const dy = (s.y - p.y) * 3;
      let dist = dx * dx + dy * dy;
      if (s.kind !== "spine") {
        // These re-wire a gateway, so they only claim a sloppy drop when the
        // pointer really is on them — but inside that radius they win.
        if (Math.hypot(s.x - p.x, s.y - p.y) > 80) return;
        dist *= 0.6;
      }
      if (dist < bestDist) {
        bestDist = dist;
        best = s;
      }
    });
    return best;
  };

  const clearDragState = () => {
    setActiveSlotKey(null);
    setDragOverCanvas(false);
    setDragOverNodeIndex(null);
    setDragOverGatewayId(null);
    setDragOverEdgeIndex(null);
    setDragItem(null);
  };

  const isTaskAttached = (idNum) =>
    workflowRef.current.some(
      (r) => !r.isChildProcess && Number(r.task_id) === Number(idNum),
    );

  /**
   * Second half of a drop on a re-wiring slot: the splice already put the step
   * in the array, this points the gateway at it so the diagram actually reaches
   * it. No-op for plain spine slots.
   */
  const applySlotLink = (slot, stepId) => {
    if (!slot || stepId == null || stepId === "") return;

    if (slot.kind === "before-gateway") {
      updateGateway(slot.gatewayId, () => ({ afterTaskId: String(stepId) }));
      return;
    }

    if (slot.kind === "branch") {
      setGateways((prev) =>
        prev.map((g) => {
          if (g.id !== slot.gatewayId) return g;
          if (slot.branchKind === "endpoint") {
            return {
              ...g,
              endpoints: (g.endpoints || []).map((ep) =>
                ep.id === slot.branchId
                  ? { ...ep, endTaskId: String(stepId) }
                  : ep,
              ),
            };
          }
          return {
            ...g,
            branches: (g.branches || []).map((b) =>
              b.id === slot.branchId
                ? {
                    ...b,
                    targetTaskId: String(stepId),
                    endEventName: "",
                    connectToEnd: b.connectToEnd !== false,
                  }
                : b,
            ),
          };
        }),
      );
    }
  };

  const attachTaskAtSlot = (taskIdNum, slot) => {
    if (!Number.isFinite(taskIdNum) || taskIdNum <= 0) return false;
    if (isTaskAttached(taskIdNum)) {
      toast.error("That task is already on this workflow.");
      return false;
    }
    handleAttachTask(taskIdNum, slot ? slot.insertIndex : null);
    applySlotLink(slot, taskIdNum);
    return true;
  };

  const moveStepToSlot = (fromIndex, slot) => {
    if (!slot || !Number.isFinite(fromIndex)) return false;
    const row = workflow[fromIndex];
    if (!row) return false;
    const stepId = stepIdOfRow(row);

    // One gateway per anchor, or the second one becomes unreachable.
    if (
      slot.kind === "before-gateway" &&
      gateways.some(
        (g) =>
          g.id !== slot.gatewayId && String(g.afterTaskId) === String(stepId),
      )
    ) {
      toast.error(
        "That step already has its own gateway attached — move a different step here.",
      );
      return false;
    }

    handleReorder(fromIndex, slot.insertIndex);
    applySlotLink(slot, stepId);
    return true;
  };

  const dropOntoSlot = (e, slot) => {
    const gatewayRefId = e.dataTransfer.getData("text/x-gateway-ref");
    if (gatewayRefId) {
      if (!slot || slot.kind !== "spine") {
        toast.error(
          "A gateway has to sit after a step — drop it on the path.",
        );
        return false;
      }
      const newAfterId = String(afterIdForIndex(slot.insertIndex));
      if (
        gateways.some(
          (g) => g.id !== gatewayRefId && String(g.afterTaskId) === newAfterId,
        )
      ) {
        toast.error(
          "That step already has its own gateway attached — drop on a different step.",
        );
        return false;
      }
      updateGateway(gatewayRefId, () => ({ afterTaskId: newAfterId }));
      return true;
    }
    const gwType = e.dataTransfer.getData("text/x-gateway-type");
    if (gwType) {
      if (!slot) return false;
      if (slot.kind !== "spine") {
        toast.error(
          "A gateway has to sit after a step — drop a task here first, then attach the gateway to it.",
        );
        return false;
      }
      addGatewayAt(afterIdForIndex(slot.insertIndex), gwType);
      return true;
    }
    const taskId = e.dataTransfer.getData("text/x-task-id");
    if (taskId) {
      return attachTaskAtSlot(Number(taskId), slot);
    }
    const rowIndexStr = e.dataTransfer.getData("text/x-row-index");
    if (rowIndexStr !== "" && rowIndexStr !== null && slot) {
      return moveStepToSlot(Number(rowIndexStr), slot);
    }
    return false;
  };

  // Drop onto a gateway diamond: inserts between the gateway and the step it
  // hangs off. "After the gateway" would be ambiguous (which branch?) — the
  // per-branch "+" handles that side.
  const dropOntoGateway = (e, gw) => {
    const slot = insertSlots.find(
      (s) => s.kind === "before-gateway" && s.gatewayId === gw.id,
    );
    if (!slot) {
      toast.error("This gateway isn't attached to the path yet.");
      return false;
    }
    return dropOntoSlot(e, slot);
  };

  // Drop onto an SVG connector: each connector owns at most one slot.
  const dropOntoEdge = (e, edgeIndex) =>
    dropOntoSlot(e, slotByEdgeIndex.get(edgeIndex) || slotForEvent(e));

  const saveError = validate();

  const onSave = async () => {
    const v = validate();
    if (v) {
      toast.error(v);
      return;
    }
    try {
      setSaving(true);

      const currentBackendIds = new Set(
        gateways.map((g) => g._backendId).filter(Boolean),
      );
      const removedBackendIds = originalGatewayBackendIdsRef.current.filter(
        (id) => !currentBackendIds.has(id),
      );
      for (const backendId of removedBackendIds) {
        try {
          await deleteGateway(backendId);
        } catch (e) {
          console.warn(`Failed to delete gateway ${backendId}:`, e);
        }
      }
      originalGatewayBackendIdsRef.current = gateways
        .map((g) => g._backendId)
        .filter(Boolean);

      const payload = {
        process_name: form.process_name,
        process_code: form.process_code,
        version: form.version === "" ? 0 : Number(form.version),
        company_id: Number(form.company_id),
        process_status_id: form.process_status_id
          ? Number(form.process_status_id)
          : null,
        process_category_id: form.process_category_id
          ? Number(form.process_category_id)
          : null,
        parent_process_id: form.parent_process_id
          ? Number(form.parent_process_id)
          : null,
        process_overview: form.process_overview,
        case_arrival_rate:
          form.case_arrival_rate === "" ? null : Number(form.case_arrival_rate),
        workflow: workflow.map((r, idx) => {
          const item = { order: idx + 1 };
          if (r.isChildProcess) {
            item.item_type = "child_process";
            item.child_process_id = Number(r.child_process_id);
          } else {
            item.item_type = "task";
            item.task_id = Number(r.task_id);
            if (r.job_id) item.job_id = Number(r.job_id);
          }
          return item;
        }),
        gateways: gateways.map((g) => {
          const gwType = String(g.type || "").toUpperCase();
          const isParallel = gwType === "PARALLEL";
          const showConditions = ["EXCLUSIVE", "INCLUSIVE", "EVENT_BASED"].includes(
            gwType,
          );

          const regularBranches = (g.branches || []).map((b) => {
            const rawTarget = b.targetTaskId;
            const isGatewayTarget =
              typeof rawTarget === "string" && rawTarget.startsWith("__GW__");
            const branch = {
              branch_pk_id:
                b._backendId != null && b._backendId !== ""
                  ? Number(b._backendId)
                  : undefined,
              targetTaskId: isGatewayTarget
                ? null
                : rawTarget
                  ? Number(rawTarget)
                  : null,
              isDefault: !!b.isDefault,
            };
            if (isGatewayTarget) {
              const ref = rawTarget.slice("__GW__".length);
              const gwIdx = gateways.findIndex((x) => String(x.id) === String(ref));
              if (gwIdx >= 0) branch.targetGatewayIndex = gwIdx;
            }
            if (showConditions) branch.condition = b.condition || "";
            if (!isGatewayTarget && b.targetTaskId) {
              branch.connectToEnd = b.connectToEnd !== false;
            } else if (!isGatewayTarget && b.endEventName) {
              branch.endEventName = b.endEventName;
            }
            return branch;
          });

          const endpointBranches = isParallel
            ? []
            : (g.endpoints || []).map((e) => {
                const endpoint = {
                  branch_pk_id:
                    e._backendId != null && e._backendId !== ""
                      ? Number(e._backendId)
                      : undefined,
                  endTaskId: e.endTaskId ? Number(e.endTaskId) : null,
                  endEventName: e.endEventName || "",
                };
                if (showConditions) endpoint.condition = e.condition || "";
                return endpoint;
              });

          const gateway = {
            gateway_pk_id:
              g._backendId != null && g._backendId !== ""
                ? Number(g._backendId)
                : undefined,
            type: (g.type || "").toUpperCase(),
            name: g.name || "",
            afterTaskId: normalizeGatewayAfterTaskId(g.afterTaskId),
            branches: [...regularBranches, ...endpointBranches],
          };

          const convergeAtTaskId = getConvergeAtTaskId(g);
          const convergeGatewayName = getConvergeGatewayName(g);
          const convergeToEnd = getConvergeToEnd(g);
          const skipConverge = exclusiveSkipsConvergence(g);

          const gwTargetIdx =
            !skipConverge && needsConvergence(g.type) && g.convergeAtGatewayId
              ? gateways.findIndex((x) => String(x.id) === String(g.convergeAtGatewayId))
              : -1;
          gateway.convergeAtGatewayIndex = gwTargetIdx >= 0 ? gwTargetIdx : undefined;
          const hasGwTarget = gateway.convergeAtGatewayIndex != null;

          gateway.convergeToEnd =
            !skipConverge && !hasGwTarget && needsConvergence(g.type) && convergeToEnd;
          const convergeNum = Number(convergeAtTaskId);
          gateway.convergeAtTaskId =
            !skipConverge &&
            !hasGwTarget &&
            needsConvergence(g.type) &&
            !gateway.convergeToEnd &&
            convergeAtTaskId &&
            Number.isFinite(convergeNum) &&
            convergeNum > 0
              ? convergeNum
              : null;
          gateway.convergeGatewayName =
            !skipConverge && needsConvergence(g.type) && String(convergeGatewayName).trim()
              ? String(convergeGatewayName).trim()
              : null;

          return gateway;
        }),
      };

      await updateProcess(processId, payload);
      // The BPMN Editor (BpmnViewerPage) caches a hand-laid-out XML diagram
      // per process, separate from this workflow/gateway data. Without
      // invalidating it here, editing the structure in Process Builder would
      // save correctly but the BPMN Editor would keep serving its stale
      // cached diagram — the two views drifting out of sync. Clearing it
      // makes the BPMN Editor regenerate fresh from what was just saved next
      // time it's opened. Best-effort: a failure here shouldn't undo the
      // workflow save that already succeeded.
      await clearStoredBpmnXml(processId).catch(() => {});
      toast.success(`Process "${form.process_name}" workflow updated!`);
      try {
        localStorage.setItem("processesNeedsRefresh", "1");
      } catch {}
      // Reload to reflect backend-assigned ids (gateway_pk_id, branch_pk_id, etc.)
      await onProcessChange(processId);
    } catch (e) {
      toast.error(e?.message || "Failed to save process workflow");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pb-page">
      <div className="pb-header">
        <div>
          <h2 className="pb-title">Process Builder</h2>
          <p className="pb-subtitle">
            Pick a company and a process, then drag tasks from the panel onto
            the workflow to attach them.
          </p>
        </div>
        <button
          className="secondary-btn"
          onClick={() => setActiveSection("process-management")}
        >
          ← Back to Processes
        </button>
      </div>

      <div className="pb-selectors role-card">
        <div className="pb-selector-field">
          <label>Company</label>
          <select
            value={companyId}
            onChange={(e) => onCompanyChange(e.target.value)}
            disabled={companyLoading}
          >
            <option value="">-- Select company --</option>
            {companies.map((c) => (
              <option key={c.company_id} value={c.company_id}>
                {c.name} {c.companyCode ? `[${c.companyCode}]` : ""}
              </option>
            ))}
          </select>
        </div>
        <div className="pb-selector-field">
          <label>Process</label>
          <select
            value={processId}
            onChange={(e) => onProcessChange(e.target.value)}
            disabled={!companyId || processesLoading}
          >
            <option value="">
              {processesLoading ? "Loading..." : "-- Select process --"}
            </option>
            {processes.map((p) => (
              <option key={p.process_id || p.id} value={p.process_id || p.id}>
                {p.process_name || p.name}{" "}
                {p.process_code || p.code ? `[${p.process_code || p.code}]` : ""}
              </option>
            ))}
          </select>
        </div>
        <div className="pb-selector-actions">
          <button
            className="primary-btn"
            onClick={openNewProcessForm}
            disabled={!companyId || companyLoading || processesLoading || creatingProcess}
          >
            New Process
          </button>
        </div>
      </div>

      {newProcessOpen && (
        <div
          className="modal-overlay"
          onClick={() => {
            if (!creatingProcess) cancelNewProcess();
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 640 }}
          >
            <div className="modal-header">
              <h3>Create Process</h3>
              <button
                className="modal-close"
                onClick={cancelNewProcess}
                disabled={creatingProcess}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="pb-new-process-grid">
                <div className="pb-selector-field">
                  <label>Process Name *</label>
                  <input
                    value={newProcessForm.process_name}
                    onChange={(e) =>
                      updateNewProcessForm("process_name", e.target.value)
                    }
                    placeholder="Untitled workflow"
                    disabled={creatingProcess}
                  />
                </div>
                <div className="pb-selector-field">
                  <label>Process Code *</label>
                  <input
                    value={newProcessForm.process_code}
                    onChange={(e) =>
                      updateNewProcessForm("process_code", e.target.value)
                    }
                    placeholder="PROC-001"
                    disabled={creatingProcess}
                  />
                </div>
                <div className="pb-selector-field">
                  <label>Version</label>
                  <input
                    value={newProcessForm.version ?? ""}
                    disabled
                    placeholder="0"
                    style={{
                      backgroundColor: "#f9fafb",
                      cursor: "not-allowed",
                      color: "#6b7280",
                    }}
                  />
                </div>
                <div className="pb-selector-field">
                  <label>Company *</label>
                  <select value={companyId} disabled>
                    {companies
                      .filter((c) => String(c.company_id) === String(companyId))
                      .map((c) => (
                        <option key={c.company_id} value={c.company_id}>
                          {c.name}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="pb-selector-field">
                  <label>Process Status</label>
                  <select
                    value={newProcessForm.process_status_id}
                    onChange={(e) =>
                      updateNewProcessForm("process_status_id", e.target.value)
                    }
                    disabled={creatingProcess}
                  >
                    <option value="">Select status</option>
                    {processStatuses.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.description || `Status ${s.id}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="pb-selector-field">
                  <label>Process Category</label>
                  <select
                    value={newProcessForm.process_category_id}
                    onChange={(e) =>
                      updateNewProcessForm(
                        "process_category_id",
                        e.target.value,
                      )
                    }
                    disabled={creatingProcess}
                  >
                    <option value="">Select process category</option>
                    {processCategories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.description || c.name || `Category ${c.id}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="pb-selector-field">
                  <label>Parent Process</label>
                  <select
                    value={newProcessForm.parent_process_id}
                    onChange={(e) =>
                      updateNewProcessForm("parent_process_id", e.target.value)
                    }
                    disabled={creatingProcess}
                  >
                    <option value="">Select parent process (optional)</option>
                    {processes.map((p) => (
                      <option
                        key={p.process_id || p.id}
                        value={p.process_id || p.id}
                      >
                        {p.process_name || p.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="pb-selector-field pb-new-process-overview">
                  <label>Overview</label>
                  <textarea
                    value={newProcessForm.process_overview}
                    onChange={(e) =>
                      updateNewProcessForm("process_overview", e.target.value)
                    }
                    placeholder="Short description for this process"
                    disabled={creatingProcess}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="secondary-btn"
                onClick={cancelNewProcess}
                disabled={creatingProcess}
              >
                Cancel
              </button>
              <button
                className="primary-btn"
                onClick={createAndOpenProcess}
                disabled={
                  creatingProcess ||
                  !newProcessForm.process_name.trim() ||
                  !newProcessForm.process_code.trim()
                }
              >
                {creatingProcess ? "Creating..." : "Create and Open"}
              </button>
            </div>
          </div>
        </div>
      )}

      {!companyId && (
        <div className="no-results" style={{ marginTop: 16 }}>
          Select a company to get started.
        </div>
      )}
      {companyId && !processId && !processesLoading && (
        <div className="no-results" style={{ marginTop: 16 }}>
          Select a process to open its workflow builder.
        </div>
      )}

      {processId && processLoading && (
        <div className="no-results" style={{ marginTop: 16 }}>
          Loading process...
        </div>
      )}

      {processId && !processLoading && form && (
        <div className="pb-workspace">
          <div className="pb-main">
            <div className="role-card">
              <div className="pb-process-heading">
                <div>
                  <div className="pb-process-name">{form.process_name}</div>
                  <div className="pb-process-code">{form.process_code}</div>
                </div>
                <button
                  className="primary-btn"
                  onClick={onSave}
                  disabled={saving}
                  title={saveError || ""}
                >
                  {saving ? "Saving..." : "Save Workflow"}
                </button>
              </div>
              {saveError && (
                <div className="pb-save-error">{saveError}</div>
              )}
              {orphanCount > 0 && (
                <div className="pb-orphan-warning">
                  {orphanCount === 1
                    ? "1 step isn't connected to the path"
                    : `${orphanCount} steps aren't connected to the path`}{" "}
                  — they're shown apart at the bottom. Drag one onto a connector,
                  or point a gateway branch at it, to bring it back in.
                </div>
              )}

              <div className="pb-diagram-instructions">
                <span>💡</span>
                <span>
                  <strong>Drop on any connector</strong> — including the ones
                  running into and out of a diamond, so steps can go before or
                  after a gateway. <strong>Click +</strong> to pick a spot
                  manually. <strong>Double-click a task</strong> to open and
                  edit its details. <strong>Click a diamond</strong> to set
                  branch logic. <strong>Drag a task box onto another task</strong>{" "}
                  to swap their positions.
                </span>
              </div>

              <div
                className={`pb-canvas ${dragOverCanvas ? "pb-drag-over" : ""}`}
                onDragOver={(e) => {
                  const kind = pathDragKind(e);
                  if (!kind) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect =
                    kind === "reorder" ? "move" : "copy";
                  setDragOverCanvas(true);
                  const slot = slotForEvent(e);
                  setActiveSlotKey(slot ? slot.key : null);
                }}
                onDragLeave={(e) => {
                  if (e.currentTarget.contains(e.relatedTarget)) return;
                  setDragOverCanvas(false);
                  setActiveSlotKey(null);
                }}
                onDrop={(e) => {
                  if (!pathDragKind(e)) return;
                  e.preventDefault();
                  dropOntoSlot(e, slotForEvent(e));
                  clearDragState();
                }}
              >
                <div
                  className="pb-canvas-inner"
                  ref={canvasInnerRef}
                  style={{ width: canvas.width, height: canvas.height }}
                >
                  <svg
                    className="pb-canvas-svg"
                    width={canvas.width}
                    height={canvas.height}
                  >
                    <defs>
                      <marker
                        id="pb-arrow"
                        markerUnits="userSpaceOnUse"
                        markerWidth="10"
                        markerHeight="10"
                        refX="7"
                        refY="3"
                        orient="auto"
                      >
                        <path d="M0,0 L7,3 L0,6 Z" fill="#1e293b" />
                      </marker>
                      <marker
                        id="pb-arrow-back"
                        markerUnits="userSpaceOnUse"
                        markerWidth="10"
                        markerHeight="10"
                        refX="7"
                        refY="3"
                        orient="auto"
                      >
                        <path d="M0,0 L7,3 L0,6 Z" fill="#f59e0b" />
                      </marker>
                    </defs>
                    {canvas.edges.map((edge, ei) => {
                      const from = canvasNodeByKey.get(edge.from);
                      const to = canvasNodeByKey.get(edge.to);
                      if (!from || !to) return null;
                      let bowY = null;
                      if (
                        edge.offSpine &&
                        !edge.backward &&
                        from.lane === to.lane &&
                        canvas.nodes.some(
                          (n) =>
                            n.lane === from.lane &&
                            n.col > Math.min(from.col, to.col) &&
                            n.col < Math.max(from.col, to.col),
                        )
                      ) {
                        // Route through whichever side of the row has no
                        // node in the way, so the corridor never overlaps
                        // another box (up is tried first — the common case
                        // is nothing routed above the main spine).
                        const spanLo = Math.min(from.x, to.x) - 10;
                        const spanHi = Math.max(from.x, to.x) + 10;
                        const isClear = (y) =>
                          !canvas.nodes.some(
                            (n) =>
                              n.x >= spanLo &&
                              n.x <= spanHi &&
                              Math.abs(n.y - y) < halfHeightOf(n.kind) + 20,
                          );
                        const up = from.y - 60;
                        const down = from.y + 60;
                        bowY = isClear(up) ? up : isClear(down) ? down : up;
                      }
                      const geo = getElbowPath(from, to, bowY);
                      const isEdgeOver = dragOverEdgeIndex === ei;
                      const stroke = isEdgeOver
                        ? "#3b82f6"
                        : edge.backward
                        ? "#f59e0b"
                        : "#1e293b";
                      const strokeW = isEdgeOver ? 4 : 2;
                      return (
                        <g key={`e-${ei}`}>
                          {/* Visible connector */}
                          <path
                            d={geo.d}
                            fill="none"
                            stroke={stroke}
                            strokeWidth={strokeW}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeDasharray={edge.backward ? "6 4" : "0"}
                            markerEnd={
                              edge.backward
                                ? "url(#pb-arrow-back)"
                                : "url(#pb-arrow)"
                            }
                            style={{ pointerEvents: "none" }}
                          />
                          {/* Wide transparent hit area so you can drop anywhere on the line */}
                          <path
                            d={geo.d}
                            fill="none"
                            stroke="transparent"
                            strokeWidth="20"
                            strokeLinecap="round"
                            style={{ cursor: "copy", pointerEvents: "stroke" }}
                            onDragOver={(e) => {
                              const kind = pathDragKind(e);
                              if (!kind) return;
                              e.preventDefault();
                              e.stopPropagation();
                              e.dataTransfer.dropEffect = kind === "reorder" ? "move" : "copy";
                              setDragOverCanvas(true);
                              setDragOverEdgeIndex(ei);
                              setDragOverNodeIndex(null);
                              setDragOverGatewayId(null);
                              // Park the drop ghost on this connector's slot.
                              const s = slotByEdgeIndex.get(ei);
                              setActiveSlotKey(s ? s.key : null);
                            }}
                            onDragLeave={() => {
                              setDragOverEdgeIndex((v) => (v === ei ? null : v));
                            }}
                            onDrop={(e) => {
                              if (!pathDragKind(e)) return;
                              e.preventDefault();
                              e.stopPropagation();
                              dropOntoEdge(e, ei);
                              clearDragState();
                            }}
                          />
                          {edge.label ? (
                            <foreignObject
                              x={geo.midX - 60}
                              y={geo.midY - 24}
                              width="120"
                              height="24"
                            >
                              <div className="pb-edge-label">{edge.label}</div>
                            </foreignObject>
                          ) : null}
                        </g>
                      );
                    })}
                  </svg>

                  {/* One "+" per connector — spine, into a gateway, and out
                      along each of its branches. */}
                  {insertSlots.map((slot) => {
                    const isOver = activeSlotKey === slot.key;
                    return (
                      <button
                        type="button"
                        key={`ins-${slot.key}`}
                        className={`pb-insert-btn ${
                          slot.kind !== "spine" ? "pb-insert-btn-gw" : ""
                        } ${isOver ? "pb-drag-over" : ""}`}
                        style={{ left: slot.x, top: slot.y }}
                        title={
                          slot.kind === "spine"
                            ? `Add a task or gateway ${describeSlot(slot)}`
                            : `Add a task ${describeSlot(slot)}`
                        }
                        onClick={() => {
                          setModalSearch("");
                          setAddModal(slot);
                        }}
                        onDragOver={(e) => {
                          const kind = pathDragKind(e);
                          if (!kind) return;
                          e.preventDefault();
                          e.stopPropagation();
                          e.dataTransfer.dropEffect =
                            kind === "reorder" ? "move" : "copy";
                          setDragOverCanvas(true);
                          setDragOverNodeIndex(null);
                          setActiveSlotKey(slot.key);
                        }}
                        onDrop={(e) => {
                          if (!pathDragKind(e)) return;
                          e.preventDefault();
                          e.stopPropagation();
                          dropOntoSlot(e, slot);
                          clearDragState();
                        }}
                      >
                        +
                      </button>
                    );
                  })}

                  {/* Ghost of what is being dragged, parked on the slot it
                      would land in. */}
                  {dragItem && activeSlot && (() => {
                    const gwOnRewireSlot =
                      dragItem.kind === "gateway" && activeSlot.kind !== "spine";
                    return (
                      <div
                        className={`pb-drop-ghost ${
                          gwOnRewireSlot ? "pb-drop-ghost-blocked" : ""
                        }`}
                        style={{ left: activeSlot.x, top: activeSlot.y - 14 }}
                      >
                        {gwOnRewireSlot
                          ? "Gateways attach after a step"
                          : `${
                              dragItem.kind === "reorder"
                                ? "Move here: "
                                : "Add here: "
                            }${dragItem.label}`}
                      </div>
                    );
                  })()}

                  {canvas.nodes.map((n) => {
                    if (n.kind === "start" || n.kind === "end") {
                      const isStart = n.kind === "start";
                      const isNamedEnd =
                        !isStart && n.name && n.name !== "End";
                      const color = isStart ? "#16a34a" : "#475569";
                      return (
                        <div
                          key={n.key}
                          className="pb-cv-endpoint"
                          style={{
                            left: n.x,
                            top: n.y,
                            borderColor: color,
                            color,
                            cursor: isStart ? "default" : "grab",
                          }}
                          draggable={!isStart}
                          onDragStart={
                            isStart
                              ? undefined
                              : (e) => {
                                  e.dataTransfer.setData("text/x-end-marker", "1");
                                  e.dataTransfer.effectAllowed = "copy";
                                }
                          }
                          title={
                            isStart
                              ? "Start"
                              : isNamedEnd
                                ? `End: ${n.name}`
                                : "End — drag onto a branch slot to route it here"
                          }
                        >
                          {isStart ? "▶" : "■"}
                          {isNamedEnd && (
                            <span className="pb-cv-endpoint-label">
                              {n.name}
                            </span>
                          )}
                        </div>
                      );
                    }

                    if (n.kind === "gateway") {
                      const info = getGatewayTypeInfo(n.gwType);
                      const gw = gateways.find((g) => g.id === n.gatewayId);
                      const isGwDragOver = dragOverGatewayId === n.gatewayId;
                      return (
                        <div
                          key={n.key}
                          className="pb-cv-gateway"
                          style={{ left: n.x, top: n.y }}
                        >
                          <div
                            className={`pb-cv-diamond ${
                              expandedGatewayId === n.gatewayId ? "pb-gw-selected" : ""
                            }${isGwDragOver ? " pb-gw-drag-over" : ""}`}
                            style={{ borderColor: isGwDragOver ? "#3b82f6" : info.color }}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData(
                                "text/x-gateway-ref",
                                String(n.gatewayId),
                              );
                              e.dataTransfer.effectAllowed = "copy";
                            }}
                            onDragOver={(e) => {
                              const kind = pathDragKind(e);
                              if (!kind || kind === "reorder") return; // reorder is task→task swap only
                              e.preventDefault();
                              e.stopPropagation();
                              e.dataTransfer.dropEffect = "copy";
                              setDragOverGatewayId(n.gatewayId);
                              setDragOverCanvas(true);
                              setActiveSlotKey(null);
                              setDragOverEdgeIndex(null);
                            }}
                            onDragLeave={() => {
                              setDragOverGatewayId((v) =>
                                v === n.gatewayId ? null : v
                              );
                            }}
                            onDrop={(e) => {
                              const kind = pathDragKind(e);
                              if (!kind || kind === "reorder") return;
                              e.preventDefault();
                              e.stopPropagation();
                              if (gw) dropOntoGateway(e, gw);
                              clearDragState();
                            }}
                            onClick={() => setExpandedGatewayId(n.gatewayId)}
                            title={`${n.name} — click to set its branch logic; drop a task here to insert it just before this gateway (use the "+" on a branch to add after it)`}
                          >
                            <span style={{ color: isGwDragOver ? "#3b82f6" : info.color }}>{info.symbol}</span>
                          </div>
                          {isGwDragOver && (
                            <div className="pb-gw-drop-hint">
                              Insert before ◇
                            </div>
                          )}
                          <span
                            className="pb-cv-gateway-name"
                            style={{ color: isGwDragOver ? "#3b82f6" : info.color }}
                          >
                            {n.name}
                          </span>
                        </div>
                      );
                    }

                    const isSub = n.kind === "subprocess";
                    const isDragOver = dragOverNodeIndex === n.trunkIndex;
                    return (
                      <div
                        key={n.key}
                        className={`pb-cv-task ${isSub ? "pb-cv-task-sub" : ""} ${
                          isDragOver ? "pb-drag-over" : ""
                        } ${n.orphan ? "pb-cv-orphan" : ""}`}
                        style={{ left: n.x, top: n.y }}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData(
                            "text/x-row-index",
                            String(n.trunkIndex),
                          );
                          e.dataTransfer.setData(
                            "text/x-workflow-ref",
                            String(
                              workflow[n.trunkIndex].isChildProcess
                                ? workflow[n.trunkIndex].child_process_id
                                : workflow[n.trunkIndex].task_id,
                            ),
                          );
                          e.dataTransfer.effectAllowed = "move";
                          setDragItem({
                            kind: "reorder",
                            label: n.name,
                            index: n.trunkIndex,
                          });
                        }}
                        onDragEnd={clearDragState}
                        // Only a step already on the path can be dropped on a
                        // box (to swap). A task or gateway coming from the side
                        // panel falls through to the canvas, which turns the
                        // box into "insert after this one".
                        onDragOver={(e) => {
                          if (pathDragKind(e) !== "reorder") return;
                          if (dragItem?.index === n.trunkIndex) return;
                          e.preventDefault();
                          e.stopPropagation();
                          e.dataTransfer.dropEffect = "move";
                          setDragOverCanvas(true);
                          setActiveSlotKey(null);
                          setDragOverNodeIndex(n.trunkIndex);
                        }}
                        onDragLeave={() =>
                          setDragOverNodeIndex((v) =>
                            v === n.trunkIndex ? null : v,
                          )
                        }
                        onDrop={(e) => {
                          const rowIndexStr = e.dataTransfer.getData(
                            "text/x-row-index",
                          );
                          if (rowIndexStr === "") return;
                          e.preventDefault();
                          e.stopPropagation();
                          const fromIndex = Number(rowIndexStr);
                          if (
                            Number.isFinite(fromIndex) &&
                            fromIndex !== n.trunkIndex
                          ) {
                            setSwapPrompt({
                              fromIndex,
                              toIndex: n.trunkIndex,
                              fromName: trunkNodeName(fromIndex),
                              toName: n.name,
                            });
                          }
                          clearDragState();
                        }}
                        onDoubleClick={() => {
                          if (isSub) {
                            toast.error(
                              "Subprocess steps are edited in their own process.",
                            );
                            return;
                          }
                          const row = workflow[n.trunkIndex];
                          if (row?.task_id)
                            setTaskDetail({ id: row.task_id, tab: "basic" });
                        }}
                        title={
                          n.orphan
                            ? `${n.name} — not connected to the path. Drag it onto a connector, or point a gateway branch at it.`
                            : isSub
                              ? n.name
                              : `${n.name} — double-click to open task details`
                        }
                      >
                        <span
                          className="pb-cv-badge"
                          style={{ background: isSub ? "#b45309" : "#2563eb" }}
                        >
                          {n.trunkIndex + 1}
                        </span>
                        <span className="pb-cv-task-name">{n.name}</span>
                        <button
                          className="pb-node-remove"
                          onClick={() => removeWorkflowRow(n.trunkIndex)}
                          title="Detach from this process's workflow"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="pb-side-panel role-card">
            <div className="pb-side-panel-header">Gateways</div>
            <div className="pb-gateway-chip-list">
              {GATEWAY_TYPE_ORDER.map((t) => {
                const info = getGatewayTypeInfo(t);
                return (
                  <div
                    key={t}
                    className="pb-gateway-chip"
                    style={{ "--gw-color": info.color }}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData("text/x-gateway-type", t);
                      e.dataTransfer.effectAllowed = "copy";
                      setDragItem({ kind: "gateway", label: info.label });
                    }}
                    onDragEnd={clearDragState}
                    title="Drag onto the path to insert this gateway after the step you drop it on"
                  >
                    <span className="pb-gateway-chip-icon">{info.symbol}</span>
                    {info.label}
                  </div>
                );
              })}
            </div>

            <div className="pb-side-panel-header pb-side-panel-header-row">
              <span>Company Tasks</span>
              <button
                type="button"
                className="secondary-btn sm"
                onClick={() => setCreateTaskSlot(null)}
                title="Create a new task for this company"
              >
                + New Task
              </button>
            </div>
            <input
              type="text"
              className="pb-task-search"
              placeholder="Search tasks..."
              value={taskSearch}
              onChange={(e) => setTaskSearch(e.target.value)}
            />
            <div className="pb-task-list">
              {availableTasks.length === 0 ? (
                <div className="no-results">
                  {tasks.length === 0
                    ? "No tasks for this company."
                    : "All tasks are attached to this workflow."}
                </div>
              ) : (
                availableTasks.map((t) => {
                  const id = t.task_id || t.id;
                  return (
                    <div
                      key={id}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData("text/x-task-id", String(id));
                        e.dataTransfer.effectAllowed = "copy";
                        setDragItem({
                          kind: "task",
                          label: t.task_name || t.name,
                        });
                      }}
                      onDragEnd={clearDragState}
                      className="pb-task-chip"
                      title="Drag onto the path to attach it after the step you drop it on"
                    >
                      <span className="pb-task-chip-name">
                        {t.task_name || t.name}
                      </span>
                      {(t.task_code || t.code) && (
                        <span className="pb-task-chip-code">
                          {t.task_code || t.code}
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {selectedGateway &&
        (() => {
          const g = selectedGateway;
          const showConditions = needsConditions(g.type);
          const skipConverge = exclusiveSkipsConvergence(g);
          const showConverge = requiresConvergence(g) && !skipConverge;
          const targetOptions = [
            { value: "", label: "■ End" },
            ...workflowTaskOptions.map((o) => ({
              value: String(o.id),
              label: o.label,
            })),
            ...gateways
              .filter((x) => x.id !== g.id)
              .map((x) => ({
                value: `__GW__${x.id}`,
                label: `◇ ${x.name || getGatewayTypeInfo(x.type).label}`,
              })),
          ];
          const convergeOptions = [
            { value: "__END__", label: "■ End" },
            ...workflowTaskOptions.map((o) => ({
              value: String(o.id),
              label: o.label,
            })),
            ...gateways
              .filter((x) => x.id !== g.id)
              .map((x) => ({
                value: `__GW__${x.id}`,
                label: `◇ ${x.name || getGatewayTypeInfo(x.type).label}`,
              })),
          ];
          const convergeValue = g.convergeToEnd
            ? "__END__"
            : g.convergeAtGatewayId
              ? `__GW__${g.convergeAtGatewayId}`
              : String(g.convergeAtTaskId || "");

          return (
            <div
              className="modal-backdrop"
              role="dialog"
              aria-modal="true"
              onClick={(e) => {
                if (e.target === e.currentTarget) closeGatewayPanel(e);
              }}
            >
              <div
                className="modal-panel modal-panel-large"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h3 className="modal-title">
                    Gateway logic — after {getPlacementLabel(g.afterTaskId)}
                  </h3>
                </div>
                <div className="modal-body">
                  <div className="pb-modal-section-title">Type</div>
                  <div className="pb-gateway-type-row">
                    {gatewayTypeChoices(g.type).map((t) => {
                      const ti = getGatewayTypeInfo(t);
                      const selected = normalizeGatewayType(g.type) === t;
                      return (
                        <button
                          key={t}
                          className={`pb-gateway-type-btn ${selected ? "selected" : ""}`}
                          style={{ "--gw-color": ti.color }}
                          onClick={() => updateGateway(g.id, () => ({ type: t }))}
                        >
                          <span>{ti.symbol}</span>
                          {ti.label}
                        </button>
                      );
                    })}
                  </div>

                  <div className="pb-modal-section-title">Name</div>
                  <input
                    type="text"
                    className="pb-gateway-name-input pb-modal-search"
                    placeholder="e.g. Is Approved?"
                    value={g.name}
                    onChange={(e) =>
                      updateGateway(g.id, () => ({ name: e.target.value }))
                    }
                  />

                  <div className="pb-modal-section-title">
                    Branches — click a chip to route each path
                  </div>
                  <div className="pb-branch-list">
                    {g.branches.map((b, idx) => {
                      const usedTargetBranchMap = new Map(
                        g.branches
                          .map((other, otherIdx) => [other, otherIdx])
                          .filter(
                            ([other]) =>
                              other.id !== b.id && other.targetTaskId,
                          )
                          .map(([other, otherIdx]) => [
                            String(other.targetTaskId),
                            otherIdx,
                          ]),
                      );
                      return (
                      <div key={b.id} className="pb-modal-branch">
                        <div className="pb-modal-branch-head">
                          <span className="pb-branch-num">{idx + 1}</span>
                          <span className="pb-modal-branch-current">
                            {b.condition ?
                              <>
                                if <strong>{b.condition}</strong> → goes to{" "}
                              </>
                            : "goes to "}
                            <strong>
                              {getSlotLabel(b.targetTaskId, b.endEventName)}
                            </strong>
                          </span>
                          <button
                            className="pb-node-remove"
                            onClick={() => removeBranch(g.id, b.id)}
                            disabled={g.branches.length <= 2}
                            title="Remove branch"
                          >
                            ×
                          </button>
                        </div>
                        <div className="pb-chip-picker">
                          {targetOptions.map((o) => {
                            const isSelected =
                              String(b.targetTaskId || "") === o.value;
                            // Empty value = shared End, which any number of
                            // branches may legitimately share.
                            const usedByBranchIdx = usedTargetBranchMap.get(
                              o.value,
                            );
                            const isDuplicate =
                              !!o.value &&
                              !isSelected &&
                              usedByBranchIdx !== undefined;
                            return (
                              <button
                                key={o.value || "__end__"}
                                className={`pb-pick-chip ${
                                  isSelected ? "selected" : ""
                                }`}
                                disabled={isDuplicate}
                                aria-disabled={isDuplicate}
                                title={
                                  isDuplicate
                                    ? `Already assigned to Branch ${usedByBranchIdx + 1}`
                                    : undefined
                                }
                                onClick={() => setBranchTarget(g.id, b.id, o.value)}
                              >
                                {o.label}
                              </button>
                            );
                          })}
                        </div>
                        <div className="pb-modal-branch-fields">
                          {!b.targetTaskId && (
                            <input
                              type="text"
                              className="pb-branch-end-name"
                              placeholder="End event name (optional)"
                              value={b.endEventName || ""}
                              onChange={(e) =>
                                updateBranchField(
                                  g.id,
                                  b.id,
                                  "endEventName",
                                  e.target.value,
                                )
                              }
                            />
                          )}
                          {showConditions && (
                            <input
                              type="text"
                              className="pb-branch-condition"
                              placeholder="Condition, e.g. approved == true"
                              value={b.condition || ""}
                              onChange={(e) =>
                                updateBranchField(
                                  g.id,
                                  b.id,
                                  "condition",
                                  e.target.value,
                                )
                              }
                            />
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                  <button
                    className="secondary-btn sm pb-add-branch-btn"
                    onClick={() => addBranch(g.id)}
                  >
                    + Add Branch
                  </button>

                  {skipConverge && (
                    <div className="pb-converge-hint">
                      No join needed: this Exclusive has continuing branch(es)
                      and a separate End path.
                    </div>
                  )}
                  {showConverge && (
                    <>
                      <div
                        className="pb-modal-section-title"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <span>
                          Paths rejoin at —{" "}
                          <strong
                            style={
                              !hasConvergeTarget(g)
                                ? { color: "#dc2626" }
                                : undefined
                            }
                          >
                            {getConvergeSlotLabel(g)}
                          </strong>
                          {!hasConvergeTarget(g) && (
                            <span
                              style={{
                                marginLeft: 8,
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#dc2626",
                              }}
                            >
                              Required
                            </span>
                          )}
                        </span>
                        {!hasConvergeTarget(g) && (
                          <button
                            type="button"
                            className="secondary-btn sm"
                            title="Pick the task right after where these branches naturally come back together (or End, if none continue)"
                            onClick={() =>
                              setConvergeTarget(g.id, suggestConvergeTarget(g))
                            }
                          >
                            Suggest join point
                          </button>
                        )}
                      </div>
                      <div className="pb-chip-picker">
                        {convergeOptions.map((o) => (
                          <button
                            key={o.value}
                            className={`pb-pick-chip ${
                              convergeValue === o.value ? "selected" : ""
                            }`}
                            onClick={() => setConvergeTarget(g.id, o.value)}
                          >
                            {o.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    className="danger-btn"
                    onClick={() => removeGateway(g.id)}
                  >
                    Delete gateway
                  </button>
                  <button
                    className="modal-btn"
                    onClick={closeGatewayPanel}
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {addModal && (
        <div
          className="modal-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={() => setAddModal(null)}
        >
          <div
            className="modal-panel modal-panel-large"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="modal-title">
                Add to path — {describeSlot(addModal)}
              </h3>
              <button
                className="modal-btn"
                onClick={() => setAddModal(null)}
              >
                Close
              </button>
            </div>
            <div className="modal-body">
              {addModal.kind === "spine" ? (
                <>
                  <div className="pb-modal-section-title">Gateway</div>
                  <div className="pb-modal-gateway-grid">
                    {GATEWAY_TYPE_ORDER.map((t) => {
                      const info = getGatewayTypeInfo(t);
                      return (
                        <button
                          key={t}
                          className="pb-modal-gateway-card"
                          style={{ "--gw-color": info.color }}
                          onClick={() => {
                            addGatewayAt(
                              afterIdForIndex(addModal.insertIndex),
                              t,
                            );
                            setAddModal(null);
                          }}
                        >
                          <span
                            className="pb-modal-gateway-icon"
                            style={{ borderColor: info.color, color: info.color }}
                          >
                            {info.symbol}
                          </span>
                          {info.label}
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="pb-modal-note">
                  A gateway always hangs off a step, so only tasks can go here.
                  Add the task first, then drop a gateway on the connector after
                  it.
                </div>
              )}

              <div className="pb-modal-section-title">Task</div>
              <button
                type="button"
                className="pb-new-task-btn"
                onClick={() => {
                  setCreateTaskSlot(addModal);
                  setAddModal(null);
                }}
              >
                <span className="pb-new-task-icon">+</span>
                <span>
                  <strong>Create a new task</strong>
                  <small>
                    Add it to {selectedCompanyName || "this company"} and place
                    it here
                  </small>
                </span>
              </button>
              <input
                type="text"
                className="pb-task-search pb-modal-search"
                placeholder="Search existing tasks..."
                value={modalSearch}
                onChange={(e) => setModalSearch(e.target.value)}
                autoFocus
              />
              <div className="pb-modal-task-list">
                {(() => {
                  const q = modalSearch.trim().toLowerCase();
                  const list = availableTasks.filter((t) => {
                    if (!q) return true;
                    const name = String(t.task_name || t.name || "").toLowerCase();
                    const code = String(t.task_code || t.code || "").toLowerCase();
                    return name.includes(q) || code.includes(q);
                  });
                  if (list.length === 0) {
                    return (
                      <div className="no-results">
                        {tasks.length === 0
                          ? "No tasks for this company."
                          : "No unattached tasks match."}
                      </div>
                    );
                  }
                  return list.map((t) => {
                    const id = t.task_id || t.id;
                    return (
                      <button
                        key={id}
                        className="pb-modal-task-row"
                        onClick={() => {
                          attachTaskAtSlot(Number(id), addModal);
                          setAddModal(null);
                        }}
                      >
                        <span className="pb-task-chip-name">
                          {t.task_name || t.name}
                        </span>
                        {(t.task_code || t.code) && (
                          <span className="pb-task-chip-code">
                            {t.task_code || t.code}
                          </span>
                        )}
                      </button>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      <TaskDetailModal
        open={taskDetail != null}
        taskId={taskDetail?.id}
        initialTab={taskDetail?.tab || "basic"}
        onClose={() => setTaskDetail(null)}
        onSaved={handleTaskSaved}
      />

      <TaskCreateModal
        open={createTaskSlot !== false}
        companyId={companyId}
        companyName={selectedCompanyName}
        onClose={() => setCreateTaskSlot(false)}
        onCreated={handleTaskCreated}
      />

      <ConfirmModal
        open={!!swapPrompt}
        title="Swap these steps?"
        message={
          swapPrompt
            ? `Put "${swapPrompt.fromName}" at position ${
                swapPrompt.toIndex + 1
              } and "${swapPrompt.toName}" at position ${
                swapPrompt.fromIndex + 1
              }? Every other step stays where it is.`
            : ""
        }
        confirmLabel="Swap"
        onConfirm={() => {
          swapWorkflowRows(swapPrompt.fromIndex, swapPrompt.toIndex);
          setSwapPrompt(null);
        }}
        onCancel={() => setSwapPrompt(null)}
      />
    </div>
  );
};

export default ProcessBuilderPage;
